import React, { useState, useEffect, useRef, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Alert,
  Badge,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tooltip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Science as ScienceIcon,
  LocalHospital as HospitalIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ChildCare as ChildCareIcon,
  ArrowBack as ArrowBackIcon,
  PlaylistAddCheck as ActionsIcon,
  LocalPrintshop as PrintLabelIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { loadLabPatients, loadLabRequestsOutpatient, loadLabRequestsInpatient, loadLabTestParameters, loadParameterDetails, saveLabResults, loadLabResults,
  loadPatientLabReport, loadPatientLabResultsToday, loadPharmacyInfo, loadPatientDocumentation, deleteResultC, generateLabResults } from '../BaseURL';
import { generateLabReportHTML } from './LabReportTemplate';
import {
  evaluate as evalFormula,
  extractIdentifiers,
  formatDerived,
  FormulaError,
  parseRange,
  formatRange,
  computeDerivedRange,
} from './formulaEval';
import Swal from 'sweetalert2';
import LabReportDialog from './LabReportDialog';
import LabPrintDialog from './LabPrintDialog';
import MedicalHistoryDialog from '../Doctors/MedicalHistoryDialog';
import { ProgressChip, AdvanceProgressButton } from '../Common/RequestProgress';
import { printSampleLabels } from './SampleLabel';

const MyPatients = () => {
  // ── Theme tokens ──────────────────────────────────────────────
  // The Enter Laboratory Results dialog used to bake mint-green pastels
  // (#f1fcf9, #c8e6c9, #1b5e20) into every surface, leaving the dark theme
  // with white strips around the modal body. These tokens swap surfaces
  // and accents to suit both themes from a single render path.
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const t = useMemo(() => ({
    isDark,
    pageBg:       isDark ? '#000000' : '#F4F6FA',
    surface:      isDark ? '#0E0E14' : '#FFFFFF',
    surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
    fieldBg:      isDark ? '#14141C' : '#FFFFFF',
    borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
    borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
    text:         isDark ? '#FFFFFF' : '#0F172A',
    textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
    textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
    rowHover:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
    accent: {
      teal:    '#11998e',   // brand
      green:   '#22c55e',
      emerald: '#10b981',
      blue:    '#3b82f6',
      amber:   '#f59e0b',
      danger:  '#ef4444',
    }
  }), [isDark]);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [quickSearchId, setQuickSearchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [patientLabs, setPatientLabs] = useState([]);
// 'hide' | 'view' | 'serve' | null — reported by the lab-request endpoints so
// the empty state can name the actual reason the list came back empty.
const [unpaidVisibility, setUnpaidVisibility] = useState(null);
// Whether results may be entered for an UNPAID order. Taken straight from the
// server (true only in 'serve' mode) rather than re-derived from
// unpaidVisibility here, so the button and the endpoint can never disagree
// about the rule.
const [canServeUnpaid, setCanServeUnpaid] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterLoading, setFilterLoading] = useState(false);

  // Patient list table state
  const [patientPage, setPatientPage] = useState(0);
  const [patientRowsPerPage, setPatientRowsPerPage] = useState(10);
  const [patientOrderBy, setPatientOrderBy] = useState('fullName');
  const [patientOrder, setPatientOrder] = useState('asc');

  // Results Entry Dialog States
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedTestForResults, setSelectedTestForResults] = useState(null);
  const [testParameters, setTestParameters] = useState([]);
  // 'parameter' (default — numeric entry with ranges/units) | 'narrative'
  // (paragraph per section + a single clinical Impression at the bottom).
  // Set from the backend response on parameter load and drives which
  // result-entry UI renders.
  const [testLabFormat, setTestLabFormat] = useState('parameter');
  // Narrative-mode local state. Keyed by section name → text.
  // Persisted to the same haematology_result table via saveLabResults,
  // packaged into the same payload shape as parameter results so the
  // backend route doesn't need a separate save endpoint.
  const [narrativeSections, setNarrativeSections] = useState({});
  const [narrativeImpression, setNarrativeImpression] = useState('');
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [parameterResult, setParameterResult] = useState('');
  const [parameterFlag, setParameterFlag] = useState('normal');

  // Which unit the technician is currently typing in for the active parameter.
  // 'primary' = the parameter.unit field on the haematology row (e.g. mg/dL)
  // 'alt'     = the parameter.alt_unit field (e.g. mmol/L)
  // Stays 'primary' for single-unit parameters and is reset every time the
  // user picks a new parameter.
  const [parameterUnitKind, setParameterUnitKind] = useState('primary');
  const [enteredResults, setEnteredResults] = useState([]);
  // Flipped to true once handleEnterResults has finished both async loads
  // (parameter catalogue + existing results). The auto-advance useEffect
  // waits for this so it doesn't pre-select a parameter that's about to
  // be marked as already-entered when loadExistingResults resolves.
  const [entryReady, setEntryReady] = useState(false);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [loadingParameterDetails, setLoadingParameterDetails] = useState(false);
  const [testCategory, setTestCategory] = useState('');
  const [availableFlags, setAvailableFlags] = useState(['Select/Enter']);
  const [editingIndex, setEditingIndex] = useState(null);
  const [generalComment, setGeneralComment] = useState('');
  const [aiResultsGenerating, setAiResultsGenerating] = useState(false);

  // Report Dialog States
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [reportLoading, setReportLoading] = useState(false);

  // Print template picker (Standard vs branded Dekyi layout)
  const [printDialogOpen, setPrintDialogOpen]           = useState(false);
  const [printReportData, setPrintReportData]           = useState({});
  const [printPatient, setPrintPatient]                 = useState(null);
  const [printCategoryInterpretations, setPrintCategoryInterpretations] = useState({});
  const [printCategoryFormats, setPrintCategoryFormats]                 = useState({});
  const [printReferringDoctor, setPrintReferringDoctor]               = useState('');
  const [printCategoryHistograms, setPrintCategoryHistograms]         = useState({});
  const [printCategoryNarratives, setPrintCategoryNarratives]         = useState({});

  // Documentation Dialog States
  const [documentationDialogOpen, setDocumentationDialogOpen] = useState(false);
  const [documentationData, setDocumentationData] = useState(null);
  const [documentationLoading, setDocumentationLoading] = useState(false);

  // Medical History Dialog State
  const [medicalHistoryOpen, setMedicalHistoryOpen] = useState(false);

  // Pagination for labs list
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Company info for reports
  const [companyInfo, setCompanyInfo] = useState(null);

  const searchInputRef = useRef(null);

  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

  // Load patients once on mount — filter is applied client-side for speed
  useEffect(() => {
    loadPatients();
  }, []);

  // Load company info on mount
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const response = await fetch(loadPharmacyInfo);
        const data = await response.json();
        if (data.success && data.results) {
          setCompanyInfo(data.results);
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };
    loadCompanyInfo();
  }, []);

  const loadPatients = async () => {
    setFilterLoading(true);
    try {
      const response = await axios.get(loadLabPatients);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('❌ Error loading patients:', error);
      setSearchResults([]);
    } finally {
      setFilterLoading(false);
    }
  };

  // Load patient labs when a *different* patient is selected. Depend on the
  // id/visitDate (primitives) rather than the whole object — merging vitals
  // into selectedPatient produces a new object ref and would otherwise loop.
  useEffect(() => {
    if (selectedPatient?.id) {
      loadPatientLabs(selectedPatient.id, selectedPatient.visitDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.id, selectedPatient?.visitDate]);

  // Check for pending lab edit when labs are loaded
  useEffect(() => {
    const pendingEdit = localStorage.getItem('pendingLabEdit');
    if (pendingEdit && patientLabs.length > 0) {
      try {
        const editData = JSON.parse(pendingEdit);
        // Find the matching lab test
        const labToEdit = patientLabs.find(lab => lab.test_name === editData.testName);
        if (labToEdit) {
          // Automatically open the results dialog for this test
          handleEnterResults(labToEdit);
        }
        localStorage.removeItem('pendingLabEdit');
      } catch (error) {
        console.error('Error parsing pending lab edit data:', error);
        localStorage.removeItem('pendingLabEdit');
      }
    }
  }, [patientLabs]);

  // Check for lab edit data from history
  useEffect(() => {
    const labEditData = localStorage.getItem('labEditData');
    if (labEditData) {
      try {
        const editData = JSON.parse(labEditData);
        // Find and select the patient
        const patientToSelect = searchResults.find(patient => patient.id === editData.patientId);
        if (patientToSelect) {
          setSelectedPatient(patientToSelect);
          // Store the edit data for later use when labs are loaded
          setTimeout(() => {
            localStorage.setItem('pendingLabEdit', JSON.stringify(editData));
          }, 100);
          // Clear the data so it doesn't trigger again
          localStorage.removeItem('labEditData');
        }
      } catch (error) {
        console.error('Error parsing lab edit data:', error);
        localStorage.removeItem('labEditData');
      }
    }
  }, [searchResults]);


  // Quick lookup by patient ID from the header — lets staff jump to another
  // patient's investigations without bouncing back to the worklist. Patient IDs
  // carry a prefix (e.g. "OPD-125"), so we match on the digits only so staff
  // can just type the number.
  const handleQuickSearchById = () => {
    const idQuery = quickSearchId.trim();
    if (!idQuery) return;

    const digitsOf = (val) => String(val ?? '').replace(/\D/g, '');
    const queryDigits = digitsOf(idQuery);

    const match = searchResults.find((p) => {
      if (String(p.id).toLowerCase() === idQuery.toLowerCase()) return true;
      const idDigits = digitsOf(p.id);
      return queryDigits && idDigits && idDigits === queryDigits;
    });

    if (match) {
      setSelectedPatient(match);
      setQuickSearchId('');
    } else {
      Swal.fire({
        icon: 'info',
        title: 'No patient found',
        text: `No patient with ID "${idQuery}" was found on the active investigations list.`,
        confirmButtonColor: '#1A9B7F',
      });
    }
  };

  const loadPatientLabs = async (patientId, visitDate) => {
  setLoading(true);

  try {
    let response;
    if (['inpatient', 'detention'].includes(String(selectedPatient.state || '').toLowerCase())) {
      // For inpatients: load labs with dynamic time filtering
      response = await axios.post(loadLabRequestsInpatient, {
        patientId,
        visitDate,
      });
    } else {
      // For outpatients: load labs with dynamic time filtering
      response = await axios.post(loadLabRequestsOutpatient, {
        patientId,
        visitDate,
      });
    }
    
    // NEW: Handle the updated response structure
    // Response now includes patient info, labRequests array, and totalRequests
    console.log('📦 [MYPATIENTS] Lab requests response:', response.data);
    
    // Capture the unpaid-visibility policy the backend reports alongside the
    // rows. Without it the empty state can only guess why the list is empty,
    // and it used to blame "active time windows" — which sent people hunting
    // through waiting-time settings when the real cause was that unpaid orders
    // are hidden. The backend already ships this on every response.
    setUnpaidVisibility(response.data?.unpaidVisibility ?? null);
    // Default true when absent: an older backend that doesn't report the policy
    // must not have its Enter Results buttons disabled across the board.
    setCanServeUnpaid(response.data?.canServeUnpaid !== false);

    if (response.data.labRequests) {
      setPatientLabs(response.data.labRequests);
      console.log('✅ [MYPATIENTS] Loaded', response.data.labRequests.length, 'lab requests');
    } else if (response.data.labRequests === undefined && Array.isArray(response.data)) {
      // Fallback for old response format (if backend not updated yet)
      setPatientLabs(response.data);
    } else {
      setPatientLabs([]);
    }

    // Merge vitals from the per-patient response into selectedPatient.
    // The list endpoint no longer ships vitals (kept the list query fast),
    // so we hydrate them here when the chart opens.
    const vitalsFromDetail = response.data?.patient?.vitals;
    if (vitalsFromDetail) {
      setSelectedPatient(prev => (prev && prev.id === patientId)
        ? { ...prev, vitalSigns: vitalsFromDetail }
        : prev);
    }
  } catch (error) {
    console.error('❌ [MYPATIENTS] Error loading patient labs:', error);
    setPatientLabs([]);
    
    Swal.fire({
      icon: 'error',
      title: 'Error Loading Labs',
      text: 'Failed to load laboratory requests. Please try again.',
      confirmButtonColor: '#d33'
    });
  } finally {
    setLoading(false);
  }
};


  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
  };

  // Normalize the state field returned by the backend ('inpatient' / 'outpatient')
  // into a stable canonical form used by the table & filters.
  const canonicalState = (raw) => {
    const s = String(raw || '').toLowerCase();
    if (s === 'inpatient' || s === 'detention') return 'InPatient';
    if (s === 'outpatient') return 'OutPatient';
    return raw || '—';
  };

  // Counts for the filter chips
  const patientCounts = useMemo(() => {
    const c = { all: searchResults.length, InPatient: 0, OutPatient: 0 };
    searchResults.forEach(p => {
      const s = canonicalState(p.state);
      if (s === 'InPatient') c.InPatient++;
      else if (s === 'OutPatient') c.OutPatient++;
    });
    return c;
  }, [searchResults]);

  // Apply search + status filter, then sort
  const visiblePatients = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    const filtered = searchResults.filter(p => {
      const s = canonicalState(p.state);
      if (filterType === 'InPatient' && s !== 'InPatient') return false;
      if (filterType === 'OutPatient' && s !== 'OutPatient') return false;
      if (!q) return true;
      return (
        (p.fullName || '').toLowerCase().includes(q)
        || String(p.id || '').toLowerCase().includes(q)
        || String(p.tel || '').includes(q)
        || (p.insurance || '').toLowerCase().includes(q)
      );
    });
    const cmp = (a, b) => {
      const av = patientOrderBy === 'state' ? canonicalState(a.state) : a[patientOrderBy];
      const bv = patientOrderBy === 'state' ? canonicalState(b.state) : b[patientOrderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    filtered.sort((a, b) => patientOrder === 'asc' ? cmp(a, b) : -cmp(a, b));
    return filtered;
  }, [searchResults, searchTerm, filterType, patientOrderBy, patientOrder]);

  const pagedPatients = visiblePatients.slice(
    patientPage * patientRowsPerPage,
    patientPage * patientRowsPerPage + patientRowsPerPage
  );

  const handlePatientSort = (field) => {
    if (patientOrderBy === field) {
      setPatientOrder(patientOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPatientOrderBy(field);
      setPatientOrder('asc');
    }
  };

  // Reset to first page if filters shrink the list past the current page
  useEffect(() => {
    if (patientPage > 0 && pagedPatients.length === 0 && visiblePatients.length > 0) {
      setPatientPage(0);
    }
  }, [pagedPatients.length, visiblePatients.length, patientPage]);

  const initialsOf = (name = '') =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

  // Results Entry Functions
  const handleEnterResults = async (lab) => {
    setSelectedTestForResults(lab);
    setResultsDialogOpen(true);
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setEnteredResults([]);
    setAvailableFlags(['Select/Enter']);
    setGeneralComment(''); // Reset general comment
    setEditingIndex(null); // Reset editing index to enable parameter selection
    setTestLabFormat('parameter');     // default until loadTestParameters tells us otherwise
    setNarrativeSections({});
    setNarrativeImpression('');
    // Gate the auto-advance effect off while the dialog is repopulating.
    // We flip it back on once BOTH async loads finish, so the auto-pick
    // can compare the full parameter list against the already-saved
    // results instead of racing the existing-results fetch.
    setEntryReady(false);

    // Load distinct parameters for this test (following Java pattern)
    const category = await loadTestParameters(lab.test_name);

    // Load existing results for this patient and test, passing both lab_no and category
    await loadExistingResults(lab, category);

    setEntryReady(true);
  };

 const loadTestParameters = async (testName) => {
  setLoadingParameters(true);

  try {
    const response = await axios.get(loadLabTestParameters, {
      params: { name: testName }
    });

    if (response.data.success) {
      setTestParameters(response.data.parameters);
      setTestCategory(response.data.category);
      // Pick up the lab's result-entry format from the backend so the
      // dialog can render either the parameter editor or the narrative
      // editor without inspecting per-row data.
      setTestLabFormat(response.data.labFormat === 'narrative' ? 'narrative' : 'parameter');
      return response.data.category;
    }

  } catch (error) {
    console.error(error);
    setTestParameters([]);
    setTestCategory('');
    return '';

  } finally {
    setLoadingParameters(false);
  }
};


 const loadExistingResults = async (lab, category) => {
  try {
    const labDate = lab.requested_date || new Date().toISOString().split('T')[0];

    const response = await axios.get(loadLabResults, {
      params: {
        patientId: selectedPatient.id,
        testCategory: category,
        date: labDate,
        labNo: lab.lab_no
      }
    });

    if (response.data.success) {
      setEnteredResults(response.data.results || []);
      setGeneralComment(response.data.generalComment || '');
      // Narrative mode: rebuild the per-section text map from the saved
      // results so the editor pre-fills every section that already has
      // text. Impression is one value per lab session.
      const byName = {};
      (response.data.results || []).forEach((r) => {
        const k = String(r.parameterName || '').trim();
        if (k) byName[k] = r.result || '';
      });
      setNarrativeSections(byName);
      setNarrativeImpression(response.data.impression || '');
    }

  } catch (error) {
    console.error(error);
    setEnteredResults([]);
    setGeneralComment('');
  }
};


 const handleParameterSelect = async (parameter) => {
  setLoadingParameterDetails(true);

  try {
    const response = await axios.get(loadParameterDetails, {
      params: {
        testName: selectedTestForResults.test_name,
        parameterName: parameter.test
      }
    });

    if (response.data.success) {
      const { parameter: paramDetails, flags = [] } = response.data;

      setSelectedParameter(paramDetails);

      // add default option first
      setAvailableFlags(['Select/Enter', ...flags]);

      setParameterResult('');
      setParameterFlag('Select/Enter');
      // Re-start in the primary unit every time the user picks a new
      // parameter; the toggle below only renders when an alt unit exists.
      setParameterUnitKind('primary');
    }

  } catch (error) {
    console.error('Error loading parameter details:', error);

    setSelectedParameter(null);
    setAvailableFlags(['Select/Enter']);

  } finally {
    setLoadingParameterDetails(false);
  }
};


  // ── Dual-unit helpers ────────────────────────────────────────────────
  // `factor` is the primary→alt multiplier stored on the parameter row.
  // The conversion is intentionally linear (no formulas), so the inverse
  // is just `1 / factor`. Both directions round to a short decimal so
  // the printed report doesn't show 14 fractional digits.
  const hasDualUnit = (param) => {
    if (!param) return false;
    const f = Number(param.unit_factor);
    return !!(param.alt_unit && Number.isFinite(f) && f > 0);
  };

  // Count the decimal places in a typed value so the converted side
  // mirrors them — "8.0" → 1 dp, "8" → 0 dp, "8.05" → 2 dp. Used to keep
  // both unit columns at the same precision on the printed report.
  const decimalPlacesOf = (str) => {
    const s = String(str ?? '').trim();
    if (!s) return null;
    const m = s.match(/\.(\d+)$/);
    if (m) return m[1].length;
    if (/^-?\d+$/.test(s)) return 0;
    return null;
  };

  // Format a converted value. When `sourceStr` is provided we mirror its
  // decimal precision — so a typed `8.0` produces an alt of `80.0` rather
  // than the magnitude-based fallback that used to return `80.00`. The
  // fallback is kept for callers that don't have a source string.
  const formatConverted = (n, sourceStr) => {
    if (!Number.isFinite(n)) return '';
    const dp = decimalPlacesOf(sourceStr);
    if (dp != null) return n.toFixed(dp);
    if (n === 0) return '0';
    const abs = Math.abs(n);
    if (abs >= 100) return n.toFixed(1);
    if (abs >= 1)   return n.toFixed(2);
    if (abs >= 0.01) return n.toFixed(3);
    return n.toFixed(4);
  };

  // Given the user's typed value + which unit they typed it in, produce
  // both string representations so the saved row is self-contained. The
  // converted side mirrors the typed side's decimal count, e.g. typing
  // `8.0 g/dL` saves the alt as `80.0 g/L`, not `80.00`.
  const buildBothUnitValues = (param, typedValue, typedKind) => {
    const empty = { primary: typedValue, alt: '' };
    if (!hasDualUnit(param)) return empty;

    const num = Number(typedValue);
    if (!Number.isFinite(num)) return empty;
    const factor = Number(param.unit_factor);

    if (typedKind === 'alt') {
      const primaryNum = num / factor;
      return { primary: formatConverted(primaryNum, typedValue), alt: String(typedValue) };
    }
    const altNum = num * factor;
    return { primary: String(typedValue), alt: formatConverted(altNum, typedValue) };
  };

  // ── Derived-parameter helpers ───────────────────────────────────────
  // A parameter is derived when the lab setup flagged it `is_derived: 1`
  // and gave it a formula. The result-entry flow can't accept manual
  // input for these; instead we recompute them every time the technician
  // adds, edits, or removes a manual result.
  const isDerivedParam = (param) => !!param?.is_derived && !!param?.formula;

  // ── Patient-context scope ───────────────────────────────────────────
  // Clinical formulas commonly need the patient's age (eGFR, paediatric
  // dosing, etc.). We expose age under several aliases (`age`, `Age`,
  // `AGE`, `ageYears`, `age_years`, plus `ageMonths` when we can derive
  // it from dob) so the lab tech writing a formula doesn't have to
  // memorise a magic name. Any future patient-derived inputs (e.g. sex
  // numerically, weight) should be added here so they're available in
  // all three scope-building sites at once.
  const buildPatientScope = (patient) => {
    const out = {};
    if (!patient) return out;

    // Primary age in years. The patient picker already exposes a
    // pre-computed `.age` integer, but we recompute from `.dob` when
    // available so the value follows the calendar rather than caching
    // the registration-day snapshot.
    let years = null;
    if (patient.dob) {
      const dob = new Date(patient.dob);
      if (!Number.isNaN(dob.getTime())) {
        const now = new Date();
        years = now.getFullYear() - dob.getFullYear();
        const md = now.getMonth() - dob.getMonth();
        if (md < 0 || (md === 0 && now.getDate() < dob.getDate())) years -= 1;
      }
    }
    if (years == null) {
      const n = Number(patient.age);
      if (Number.isFinite(n)) years = n;
    }
    if (Number.isFinite(years)) {
      out.age = years;
      out.Age = years;
      out.AGE = years;
      out.ageYears = years;
      out.age_years = years;
    }

    // Months — useful for paediatric reference ranges and weight-based
    // dosing formulas. Only emitted when dob is parseable; otherwise
    // formulas referencing it will raise "Missing value for ageMonths"
    // which is the correct behaviour for a record without dob.
    if (patient.dob) {
      const dob = new Date(patient.dob);
      if (!Number.isNaN(dob.getTime())) {
        const now = new Date();
        const months = (now.getFullYear() - dob.getFullYear()) * 12
                     + (now.getMonth() - dob.getMonth())
                     - (now.getDate() < dob.getDate() ? 1 : 0);
        if (Number.isFinite(months) && months >= 0) {
          out.ageMonths = months;
          out.age_months = months;
        }
      }
    }
    return out;
  };

  // Identifiers we silently provide. `derivedPendingInputs` excludes
  // these so the UI doesn't display "needs age" when age is already
  // available from the patient record.
  const PATIENT_SCOPE_KEYS = new Set([
    'age', 'Age', 'AGE', 'ageYears', 'age_years', 'ageMonths', 'age_months',
  ]);

  // Pure function: given the current list of manual results + the lab's
  // parameter catalog, returns the same list with every computable
  // derived row added (or refreshed) at the bottom in catalog order.
  // Pending derived rows (missing inputs) are omitted entirely so the
  // table only shows finalised values.
  const recomputeDerivedResults = (currentResults, parameters, gender, patient) => {
    if (!Array.isArray(parameters) || parameters.length === 0) return currentResults;
    const manualByName = new Map();
    for (const r of currentResults) {
      if (!r?.derived) manualByName.set(r.parameterName, r);
    }
    const derivedParams = parameters.filter(isDerivedParam);
    if (derivedParams.length === 0) return currentResults.filter(r => !r?.derived);

    // Scope seeded with the patient context FIRST (age, ageMonths, etc.)
    // then MANUAL values overlay it so a measured parameter named "age"
    // — unlikely but defensible — would still win. Derived values are
    // folded in below as each one resolves so a calculated parameter
    // can feed another.
    const scope = { ...buildPatientScope(patient) };
    for (const r of currentResults) {
      if (r?.derived) continue;
      const n = Number(r.result);
      if (Number.isFinite(n)) scope[r.parameterName] = n;
    }

    // Reference-range map keyed by parameter name. Seeded with the parsed
    // ranges of every manual row so derived ranges can be propagated through
    // the formula: e.g. `MCH = HGB / RBC * 10` produces a derived range
    // computed from the corners of the HGB and RBC ranges. Manual rows that
    // arrived without a range (newly added by the technician before they
    // typed one) fall back to the parameter catalogue.
    const rangeByName = new Map();
    const pickCatalogRange = (param) => {
      const g = String(gender || '').toUpperCase();
      if (g === 'MALE'   && param?.male)   return param.male;
      if (g === 'FEMALE' && param?.female) return param.female;
      if (param?.children) return param.children;
      return '';
    };
    const paramByName = new Map(parameters.map((p) => [p.test, p]));
    for (const r of currentResults) {
      if (r?.derived) continue;
      const fromRow = parseRange(r.range);
      if (fromRow) { rangeByName.set(r.parameterName, fromRow); continue; }
      // Fall back to the catalogue range for this parameter when the row
      // itself doesn't carry one (e.g. legacy rows or freshly-typed rows
      // before the catalogue lookup populated `range`).
      const fromCat = parseRange(pickCatalogRange(paramByName.get(r.parameterName)));
      if (fromCat) rangeByName.set(r.parameterName, fromCat);
    }

    // Build a per-param row factory once so the resolution loop stays compact.
    // `computedRange` is the range we propagated through the formula (when
    // possible); we fall back to the parameter's own catalogue range so the
    // user can still override it manually on the lab setup form.
    const buildRow = (dp, value, computedRange) => {
      const formatted = formatDerived(value);
      const catalogRange = pickCatalogRange(dp);
      // Prefer the explicitly set catalog range. Only fall back to the
      // formula-propagated range if the lab tech left the catalog entry blank.
      const range = (catalogRange && catalogRange !== '')
        ? catalogRange
        : (computedRange ? formatRange(computedRange) : 'Not specified');
      let flag = 'normal';
      try { flag = detectFlagFromResult(formatted, range); } catch (_) { /* keep 'normal' */ }
      const prev = manualByName.get(dp.test);
      return {
        parameterName: dp.test,
        result: formatted,
        result_alt: '',
        unit: dp.unit || '',
        unit_alt: '',
        entered_unit_kind: null,
        range,
        flag,
        derived: true,
        formula: dp.formula,
        ...(prev?.trans_code ? { trans_code: prev.trans_code } : {}),
      };
    };

    // Iteratively resolve derived parameters. Each pass tries every still-
    // pending row; any that evaluates successfully adds its value to the
    // scope so the next pass can resolve params that depend on it. We stop
    // when a full pass produces no progress — either everything resolved,
    // or what's left is genuinely missing inputs / part of a cycle. Worst-
    // case bound: O(N²) where N = number of derived params, which is fine
    // for the handful of derived rows a lab uses in practice.
    const resolved = new Map(); // dp.test -> row
    const pending = new Map();  // dp.test -> dp
    for (const dp of derivedParams) pending.set(dp.test, dp);

    let progressed = true;
    while (progressed && pending.size > 0) {
      progressed = false;
      for (const [name, dp] of pending) {
        let value;
        try {
          value = evalFormula(dp.formula, scope);
        } catch (_) {
          // Missing input / div-by-zero / non-numeric → try again on the
          // next pass once another derived row may have populated the scope.
          continue;
        }
        if (!Number.isFinite(value)) continue;
        scope[name] = value;
        // Propagate the input ranges through the formula. If every input
        // range is known (and the formula evaluates cleanly at the corner
        // points), we get a derived range that's stored on the row and
        // available for downstream derived params.
        const derivedRange = computeDerivedRange(dp.formula, rangeByName);
        if (derivedRange) rangeByName.set(name, derivedRange);
        resolved.set(name, buildRow(dp, value, derivedRange));
        pending.delete(name);
        progressed = true;
      }
    }

    // Emit resolved rows in catalogue order so the table doesn't reshuffle
    // every time a derived value changes. Unresolved rows are dropped —
    // the right-panel "needs X" hint already covers the pending case.
    const computedRows = [];
    for (const dp of derivedParams) {
      if (resolved.has(dp.test)) computedRows.push(resolved.get(dp.test));
    }

    const stripped = currentResults.filter(r => !r?.derived);
    return [...stripped, ...computedRows];
  };

  // Auto-refresh derived rows whenever any input row changes. Skipped
  // when no derived parameters exist so we don't pay for an extra render.
  useEffect(() => {
    if (!testParameters || testParameters.length === 0) return;
    if (!testParameters.some(isDerivedParam)) return;
    const next = recomputeDerivedResults(
      enteredResults,
      testParameters,
      selectedPatient?.gender,
      selectedPatient,
    );
    // Shallow-compare results lengths + flags to avoid an infinite loop
    // when nothing actually changed.
    const same =
      next.length === enteredResults.length &&
      next.every((r, i) => {
        const cur = enteredResults[i];
        return cur &&
          cur.parameterName === r.parameterName &&
          cur.result === r.result &&
          cur.flag === r.flag &&
          !!cur.derived === !!r.derived;
      });
    if (!same) setEnteredResults(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enteredResults, testParameters, selectedPatient?.gender, selectedPatient?.dob, selectedPatient?.age]);

  // ── Auto-advance through parameters ─────────────────────────────────
  // The technician's flow is: open a lab → enter the first parameter →
  // hit Add → enter the next → … etc. To save them from clicking each
  // parameter row manually, we auto-select the next pending parameter
  // every time the form clears (initial open + after Add).
  //
  // Rules the auto-pick must respect:
  //   • Skip derived parameters — they're computed from the manual values
  //     and added to the table by the recompute loop above. Selecting one
  //     just blocks the entry field.
  //   • Skip parameters that are already in `enteredResults` — covers both
  //     freshly-typed rows in this session and rows that came back from
  //     loadExistingResults (re-entering a saved lab).
  //   • Don't override the technician's pick. If selectedParameter is set
  //     (clicked manually) or editingIndex is non-null (the row is being
  //     patched in place), the effect bails.
  //
  // `autoPickInFlightRef` guards against double-firing while
  // handleParameterSelect's axios call is still resolving — both
  // testParameters and enteredResults can change in quick succession
  // during the initial dialog load.
  const autoPickInFlightRef = useRef(false);
  useEffect(() => {
    if (!resultsDialogOpen) return;
    // Wait for both the parameter list and the existing-results fetch.
    // Without this gate the effect can fire as soon as testParameters
    // populates (before enteredResults), and pre-select a parameter that
    // the technician has already saved in a previous session.
    if (!entryReady) return;
    if (selectedParameter) return;
    if (editingIndex !== null) return;
    if (autoPickInFlightRef.current) return;
    if (!Array.isArray(testParameters) || testParameters.length === 0) return;

    const enteredNames = new Set(enteredResults.map((r) => r.parameterName));
    const next = testParameters.find(
      (p) => !isDerivedParam(p) && !enteredNames.has(p.test)
    );
    if (!next) return;

    autoPickInFlightRef.current = true;
    Promise.resolve(handleParameterSelect(next)).finally(() => {
      autoPickInFlightRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsDialogOpen, entryReady, testParameters, enteredResults, selectedParameter, editingIndex]);

  // For the "pending" pill on the right panel: which referenced inputs are
  // currently empty? Used to render "Pending: needs HGB, RBC" so the user
  // knows what to enter before the derived value will appear. Patient-
  // supplied identifiers (age, ageMonths, etc.) are treated as already-
  // present so the formula doesn't appear stuck on something the system
  // computes automatically.
  const derivedPendingInputs = (param) => {
    if (!isDerivedParam(param)) return [];
    let ids;
    try { ids = extractIdentifiers(param.formula); } catch (_) { return []; }
    const patientKeys = new Set(Object.keys(buildPatientScope(selectedPatient)));
    const have = new Set(
      enteredResults
        .filter(r => Number.isFinite(Number(r?.result)))
        .map(r => r.parameterName)
    );
    return ids.filter(n => !have.has(n) && !patientKeys.has(n));
  };

  // The computed value (or '' when pending). Used by the read-only Result
  // Value display on the right panel when a derived parameter is picked.
  const derivedComputedValue = (param) => {
    if (!isDerivedParam(param)) return '';
    // Patient context goes in first so an "age"-using formula resolves
    // even before the technician has typed any measured values.
    const scope = { ...buildPatientScope(selectedPatient) };
    for (const r of enteredResults) {
      const n = Number(r?.result);
      if (Number.isFinite(n)) scope[r.parameterName] = n;
    }
    try { return formatDerived(evalFormula(param.formula, scope)); }
    catch (_) { return ''; }
  };

  const handleAddResult = () => {
    // Derived parameters can't be added manually — they get auto-added
    // by recomputeDerivedResults once their inputs are filled.
    if (isDerivedParam(selectedParameter)) {
      Swal.fire({
        icon: 'info',
        title: 'This parameter is calculated',
        html: `<b>${selectedParameter.test}</b> is a derived parameter — its value is computed automatically from <code>${selectedParameter.formula}</code>.<br/><br/>Enter the inputs and the result will appear in the Added Results table on its own.`,
        confirmButtonColor: '#11998e',
      });
      return;
    }

    if (!selectedParameter || !parameterResult.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a parameter and enter a result.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const both = buildBothUnitValues(selectedParameter, parameterResult, parameterUnitKind);
    const isDual = hasDualUnit(selectedParameter);

    const newResult = {
      parameterName: selectedParameter.test,
      // Normalize so the lab record always carries the primary-unit value
      // in `result`. Reports + history queries already key off this field.
      result: both.primary,
      result_alt: isDual ? both.alt : '',
      unit: selectedParameter.unit || '',
      unit_alt: isDual ? (selectedParameter.alt_unit || '') : '',
      entered_unit_kind: isDual ? parameterUnitKind : null,
      range: getParameterRange(selectedParameter),
      flag: parameterFlag
    };

    if (editingIndex !== null) {
      // Update existing result - preserve trans_code for database update
      const resultToUpdate = enteredResults[editingIndex];
      if (resultToUpdate.trans_code) {
        newResult.trans_code = resultToUpdate.trans_code;
      }
      const updatedResults = [...enteredResults];
      updatedResults[editingIndex] = newResult;
      setEnteredResults(updatedResults);
      setEditingIndex(null);
    } else {
      // Check for duplicate parameter (only when adding new)
      const isDuplicate = enteredResults.some(result => result.parameterName === selectedParameter.test);
      if (isDuplicate) {
        Swal.fire({
          icon: 'warning',
          title: 'Duplicate Parameter',
          text: 'This parameter has already been added. Use edit to modify the existing result.',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      // Add new result
      setEnteredResults([...enteredResults, newResult]);
    }

    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setAvailableFlags(['Select/Enter']);
    setParameterUnitKind('primary');
  };

  const getParameterRange = (parameter) => {
    // Logic to determine range based on patient demographics (following Java pattern)
    const patientGender = selectedPatient?.gender?.toUpperCase();

    if (patientGender === 'MALE' && parameter.male) {
      return parameter.male;
    } else if (patientGender === 'FEMALE' && parameter.female) {
      return parameter.female;
    } else if (parameter.children) {
      return parameter.children;
    } else {
      return 'Not specified';
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  Intelligent reference-range parser
  //
  //  Real-world reference ranges show up in a dozen different shapes:
  //
  //    "13.5 - 17.5"      classic numeric range
  //    "13.5–17.5"        en-dash / em-dash / minus sign
  //    "13.5 to 17.5"     written-out "to"
  //    "< 200"            upper bound only
  //    "> 60"             lower bound only
  //    "≤ 5" / "<= 5"     upper bound (inclusive)
  //    "≥ 60" / ">= 60"   lower bound (inclusive)
  //    "Up to 5"          synonym for "≤ 5"
  //    "At least 60"      synonym for "≥ 60"
  //    "40 mg/dL"         single threshold (treat as expected value)
  //    "Negative"         qualitative — match string equality
  //    " 13.5 - 17.5 g/dL" any of the above with stray units appended
  //
  //  All of those need to map a numeric (or textual) result into one of:
  //    'low' | 'normal' | 'high' | 'abnormal' | 'Select/Enter'
  //
  //  The caller then asks `pickAvailableFlag` to translate that canonical
  //  token into whichever flag string the parameter has configured in the
  //  database (e.g. 'Normal', 'High', 'LOW') — case-insensitively.
  // ──────────────────────────────────────────────────────────────────────────

  // Strip stray units / suffixes / unicode dashes so the regexes below stay
  // focused on the numeric structure of the range. Lowercase makes word-form
  // patterns ("to", "up to", "at least") case-insensitive.
  const normaliseRangeString = (r) => String(r || '')
    .replace(/[\u2013\u2014\u2212]/g, '-')   // –, —, − → -
    .replace(/\s+to\s+/gi, ' - ')            // "5 to 10" → "5 - 10"
    .replace(/[\u2264]/g, '<=')              // ≤ → <=
    .replace(/[\u2265]/g, '>=')              // ≥ → >=
    .replace(/[\u2248~]/g, '')               // ≈, ~ (approx) — strip
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  // Pull the first signed decimal number out of a string ("4.2 mg/dL" → 4.2).
  const extractNumber = (s) => {
    const m = String(s || '').match(/-?\d+(?:\.\d+)?/);
    return m ? parseFloat(m[0]) : NaN;
  };

  const detectFlagFromResult = (result, range) => {
    // Empty result → prompt the user to pick a flag manually.
    if (!result || !String(result).trim()) {
      return 'Select/Enter';
    }
    const rawResult = String(result).trim();
    const numericResult = extractNumber(rawResult);

    // No usable range → fall back to normal; the user can still override.
    if (!range || String(range).trim() === '' || String(range).toLowerCase() === 'not specified') {
      return 'normal';
    }

    const rng = normaliseRangeString(range);

    // ── Qualitative reference (e.g. "negative", "non-reactive") ──────────
    // If the range has no digits at all, treat it as a string comparison.
    if (!/\d/.test(rng)) {
      const expected = rng.replace(/[^a-z\s-]/g, '').trim();
      const actual = rawResult.toLowerCase().replace(/[^a-z\s-]/g, '').trim();
      if (!actual) return 'normal';
      return actual === expected ? 'normal' : 'abnormal';
    }

    // For every numeric pattern below we need a numeric result. If the user
    // typed words against a numeric range we can't compare, so just default
    // to 'normal' and let them choose manually.
    if (isNaN(numericResult)) {
      return 'normal';
    }

    // ── Pattern 1: classic two-sided range "x - y" ───────────────────────
    // Handled first so a range like "0.5 - 1.5" doesn't get caught by the
    // single-sided patterns below.
    const rangeMatch = rng.match(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/);
    if (rangeMatch) {
      const lo = parseFloat(rangeMatch[1]);
      const hi = parseFloat(rangeMatch[2]);
      if (!isNaN(lo) && !isNaN(hi) && hi >= lo) {
        if (numericResult < lo) return 'low';
        if (numericResult > hi) return 'high';
        return 'normal';
      }
    }

    // ── Pattern 2: inclusive upper bound ("<= x", "up to x", "max x") ────
    const leqMatch = rng.match(/(?:<=|up to|maximum|max)\s*(-?\d+(?:\.\d+)?)/);
    if (leqMatch) {
      const hi = parseFloat(leqMatch[1]);
      if (!isNaN(hi)) return numericResult <= hi ? 'normal' : 'high';
    }

    // ── Pattern 3: inclusive lower bound ("≥ x", "at least x", "min x") ──
    const geqMatch = rng.match(/(?:>=|at least|minimum|min)\s*(-?\d+(?:\.\d+)?)/);
    if (geqMatch) {
      const lo = parseFloat(geqMatch[1]);
      if (!isNaN(lo)) return numericResult >= lo ? 'normal' : 'low';
    }

    // ── Pattern 4: strict upper bound ("< x", "below x", "under x") ──────
    const ltMatch = rng.match(/(?:<|less than|below|under)\s*(-?\d+(?:\.\d+)?)/);
    if (ltMatch) {
      const hi = parseFloat(ltMatch[1]);
      if (!isNaN(hi)) return numericResult < hi ? 'normal' : 'high';
    }

    // ── Pattern 5: strict lower bound ("> x", "greater than x", "above x") ─
    const gtMatch = rng.match(/(?:>|greater than|above|over|more than)\s*(-?\d+(?:\.\d+)?)/);
    if (gtMatch) {
      const lo = parseFloat(gtMatch[1]);
      if (!isNaN(lo)) return numericResult > lo ? 'normal' : 'low';
    }

    // ── Pattern 6: a single bare number (treat as the expected value) ────
    // Common for things like "12" with no operator — accept ±10% as normal.
    const singleMatch = rng.match(/(-?\d+(?:\.\d+)?)/);
    if (singleMatch) {
      const target = parseFloat(singleMatch[1]);
      if (!isNaN(target)) {
        if (target === 0) {
          return numericResult === 0 ? 'normal' : 'abnormal';
        }
        const tolerance = Math.abs(target) * 0.10;
        if (numericResult < target - tolerance) return 'low';
        if (numericResult > target + tolerance) return 'high';
        return 'normal';
      }
    }

    return 'normal';
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  Range bounds extractor — used by the visual <RangeScale/> below.
  //  Returns {min, max, kind} where kind is one of:
  //     'range'   — two-sided "x - y"
  //     'leq'     — upper bound only ("< x", "<= x", "up to x")
  //     'geq'     — lower bound only ("> x", ">= x", "at least x")
  //     'single'  — a bare expected value
  //     null      — qualitative or unparseable (caller hides the scale)
  // ──────────────────────────────────────────────────────────────────────────
  const extractRangeBounds = (range) => {
    if (!range || String(range).trim() === '' || String(range).toLowerCase() === 'not specified') return null;
    const rng = normaliseRangeString(range);
    if (!/\d/.test(rng)) return null;

    // Two-sided "x - y" — try first so single-number patterns don't steal it.
    const r = rng.match(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/);
    if (r) {
      const lo = parseFloat(r[1]);
      const hi = parseFloat(r[2]);
      if (!isNaN(lo) && !isNaN(hi) && hi > lo) return { min: lo, max: hi, kind: 'range' };
    }

    const leq = rng.match(/(?:<=|<|less than|below|under|up to|maximum|max)\s*(-?\d+(?:\.\d+)?)/);
    if (leq) return { min: null, max: parseFloat(leq[1]), kind: 'leq' };

    const geq = rng.match(/(?:>=|>|greater than|above|over|more than|at least|minimum|min)\s*(-?\d+(?:\.\d+)?)/);
    if (geq) return { min: parseFloat(geq[1]), max: null, kind: 'geq' };

    const single = rng.match(/(-?\d+(?:\.\d+)?)/);
    if (single) {
      const v = parseFloat(single[1]);
      if (!isNaN(v)) return { min: v * 0.9, max: v * 1.1, kind: 'single' };
    }
    return null;
  };

  // Pretty-print a number for the scale labels — strips trailing zeros so
  // "3.50" → "3.5" but keeps integer values as integers.
  const fmtNum = (n) => {
    if (!Number.isFinite(n)) return '—';
    const s = n.toString();
    return s.includes('.') ? s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') : s;
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  <RangeScale/> — small SVG that mimics the printed lab-report bar.
  //
  //  Layout: cream low-zone | green normal-zone | cream high-zone
  //  Pointer: down-arrow with the entered value labelled above it.
  //  Below the bar: the min / max numbers from the reference range.
  //
  //  The visible window is widened ±15% beyond the bounds so a slightly out-
  //  of-range value still lands inside the SVG (no clipping). The arrow is
  //  clamped so it never escapes the bar entirely.
  // ──────────────────────────────────────────────────────────────────────────
  const RangeScale = ({ result, range, flag }) => {
    const bounds = extractRangeBounds(range);
    if (!bounds) return <Box sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>—</Box>;

    // Pull the first signed number out of the typed result.
    const m = String(result || '').match(/-?\d+(?:\.\d+)?/);
    const value = m ? parseFloat(m[0]) : NaN;
    if (!Number.isFinite(value)) {
      return <Box sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>—</Box>;
    }

    // Build a fake [min, max] window for one-sided ranges so the green zone
    // has a visible size. We anchor the bound to ±40% of itself.
    let min = bounds.min;
    let max = bounds.max;
    if (bounds.kind === 'leq') { min = max * 0.6; }
    if (bounds.kind === 'geq') { max = min * 1.4; }
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return <Box sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>—</Box>;
    }

    const span = max - min;
    const visibleMin = min - span * 0.20;
    const visibleMax = max + span * 0.20;
    const ratio = (value - visibleMin) / (visibleMax - visibleMin);
    const clamped = Math.max(0, Math.min(1, ratio));

    // SVG geometry — keep this tight so it fits inside a TableCell.
    const W = 170;
    const H = 38;
    const barY = 18;
    const barH = 10;
    const cream = '#fef3c7';        // muted yellow for out-of-range edges
    const greenZoneStart = 0.15;     // ~15% cream on each side, 70% green band
    const greenZoneEnd   = 0.85;

    const isLow      = String(flag).toLowerCase() === 'low';
    const isHigh     = String(flag).toLowerCase() === 'high';
    const isAbnormal = String(flag).toLowerCase() === 'abnormal';
    const isNormal   = !isLow && !isHigh && !isAbnormal;

    const accent = isNormal   ? '#22c55e'
                 : isHigh     ? '#ef4444'
                 : isLow      ? '#3b82f6'
                 : /* abn */    '#f59e0b';

    const markerX = clamped * W;
    const labelW  = 38;
    // Keep the value chip inside the SVG even at the extremes.
    const labelX  = Math.max(labelW / 2, Math.min(W - labelW / 2, markerX));

    return (
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch', minWidth: W }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
          {/* Reference bar — left cream, green normal zone, right cream */}
          <rect x={0}                       y={barY} width={W * greenZoneStart}             height={barH} rx={3} ry={3} fill={cream} />
          <rect x={W * greenZoneStart}      y={barY} width={W * (greenZoneEnd - greenZoneStart)} height={barH} fill={'#22c55e'} />
          <rect x={W * greenZoneEnd}        y={barY} width={W * (1 - greenZoneEnd)}         height={barH} rx={3} ry={3} fill={cream} />

          {/* Down-pointing marker triangle, coloured by the detected status */}
          <polygon
            points={`${markerX - 5},${barY - 4} ${markerX + 5},${barY - 4} ${markerX},${barY + 1}`}
            fill={accent}
            stroke="#0b0b0b"
            strokeOpacity={0.25}
            strokeWidth={0.5}
          />

          {/* Value chip — small pill above the marker */}
          <g transform={`translate(${labelX - labelW / 2}, 0)`}>
            <rect x={0} y={0} width={labelW} height={13} rx={2.5} ry={2.5}
                  fill={accent} opacity={0.95} />
            <text
              x={labelW / 2}
              y={9.5}
              fontSize={9}
              fontWeight={800}
              textAnchor="middle"
              fill="#ffffff"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {fmtNum(value)}
            </text>
          </g>
        </svg>

        {/* Min / max labels under the bar, aligned to the green-zone edges */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: `${W * greenZoneStart}px`,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.66rem',
          fontWeight: 700,
          color: 'text.secondary',
          mt: '-2px',
        }}>
          <span>{bounds.kind === 'leq' ? '' : fmtNum(bounds.min ?? min)}</span>
          <span>{bounds.kind === 'geq' ? '' : fmtNum(bounds.max ?? max)}</span>
        </Box>
      </Box>
    );
  };

  // Map the canonical token returned by detectFlagFromResult to whichever
  // flag string the parameter actually has configured. Lab admins set up
  // flags as 'Normal', 'NORMAL', 'low', 'Low' etc. inconsistently — this
  // tries common synonyms so the dropdown always lands on the right entry.
  const pickAvailableFlag = (canonical, flagsList) => {
    if (!canonical || canonical === 'Select/Enter') return 'Select/Enter';
    const flags = Array.isArray(flagsList) ? flagsList : [];
    const want = String(canonical).toLowerCase();

    // Synonyms an admin might have typed instead of the canonical word.
    const synonyms = {
      low:      ['low', 'lo', 'decreased', 'reduced', 'below normal', 'l'],
      high:     ['high', 'hi', 'elevated', 'increased', 'above normal', 'h'],
      normal:   ['normal', 'nrm', 'within range', 'wnl', 'within normal limits', 'n'],
      abnormal: ['abnormal', 'positive', 'reactive', 'detected', 'present', 'abn'],
    };
    const candidates = synonyms[want] || [want];

    for (const cand of candidates) {
      const hit = flags.find((f) => String(f).toLowerCase() === cand);
      if (hit) return hit;
    }
    // No configured match — fall back to the canonical token itself so the
    // detection at least surfaces something the user can see (the dropdown
    // will show 'low'/'high'/'normal' even if it's not in the list).
    return canonical;
  };

  const handleFlagChange = (value) => {
    setParameterFlag(value);

    // If range is "Not specified", set result to the selected flag value
    if (selectedParameter) {
      const range = getParameterRange(selectedParameter);
      if (range === 'Not specified') {
        setParameterResult(value); // Result becomes the flag value
      }
    }
  };

  const handleResultChange = (value) => {
    setParameterResult(value);

    // Auto-detect flag based on result and range (only for parameters with ranges).
    // We resolve the canonical token from the parser to whichever flag string the
    // parameter actually exposes via `availableFlags`, so case + synonym variants
    // ('Low', 'lo', 'Decreased', etc.) still land on the configured entry.
    if (selectedParameter) {
      const range = getParameterRange(selectedParameter);
      if (range !== 'Not specified') {
        const canonical = detectFlagFromResult(value, range);
        const resolved = pickAvailableFlag(canonical, availableFlags);
        setParameterFlag(resolved);
      }
      // For parameters with no range, flag determines the result, not vice versa
    }
  };

  const handleClearResult = () => {
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setAvailableFlags(['Select/Enter']);
    setParameterUnitKind('primary');
  };

  const handleSaveResults = async () => {
    // Narrative labs build their payload from the local sections map +
    // impression instead of the parameter `enteredResults` array. Each
    // section becomes one row with `result` = paragraph text. Same DB
    // table; the entry/report UI just branches on lab_format.
    const isNarrative = testLabFormat === 'narrative';
    const payloadResults = isNarrative
      ? testParameters.map((p) => {
          // Preserve trans_code from any existing result row for the same
          // section so the save path UPDATEs instead of duplicating.
          const existing = enteredResults.find((r) => r.parameterName === p.test);
          return {
            parameterName: p.test,
            result:        (narrativeSections[p.test] || '').trim(),
            flag:          'normal',
            unit:          '',
            range:         '',
            category:      p.cat || testCategory,
            trans_code:    existing?.trans_code || '-',
          };
        }).filter((r) => r.result.length > 0 || r.trans_code !== '-')
      : enteredResults;

    if (payloadResults.length === 0 && !isNarrative) {
      Swal.fire({
        icon: 'warning',
        title: 'No Results to Save',
        text: 'Please add at least one test result before saving.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }
    if (isNarrative && payloadResults.length === 0 && !narrativeImpression.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nothing to save',
        text: 'Type at least one section description (or an Impression) before saving.',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Confirm Save',
      text: `Are you sure you want to save ${payloadResults.length} ${isNarrative ? 'section description(s)' : 'test result(s)'} for ${selectedTestForResults.test_name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save Results',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    // Show loading dialog
    Swal.fire({
      title: 'Saving Results...',
      text: 'Please wait while we save your test results.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await axios.post(saveLabResults, {
        patientId: selectedPatient.id,
        testName: selectedTestForResults.test_name,
        testCategory: testCategory, // Include test category for proper result categorization
        lab_no: selectedTestForResults.lab_no,
        results: payloadResults,
        enteredBy: userInfo.name ,
        // Always stamp the result with TODAY's date — even when the lab was
        // requested days ago and is being entered now under the 7-day
        // pending-window rule. This is what makes the report appear as if
        // the test was run today (so reports always reflect the actual
        // testing date, not the requisition date).
        enteredDate: new Date().toISOString().slice(0, 10),
        generalComment: generalComment, // Add general comment
        impression: isNarrative ? narrativeImpression.trim() : '',
        patientInfo: {
          name: selectedPatient.fullName,
          age: selectedPatient.age,
          gender: selectedPatient.gender,
          path_no: selectedPatient.id,
          other_comments: ''
        }
      });

      if (response.data.success) {
        
        Swal.fire({
          icon: 'success',
          title: 'Results Saved Successfully!',
          text: `${enteredResults.length} test result(s) have been saved.`,
          confirmButtonColor: '#3085d6',
          timer: 3000,
          timerProgressBar: true
        });

        setResultsDialogOpen(false);
        setSelectedTestForResults(null);
        setEnteredResults([]);
        setGeneralComment(''); // Reset general comment
        setTestParameters([]); // Clear test parameters
        setTestCategory(''); // Clear test category
        setSelectedParameter(null); // Clear selected parameter
        setParameterResult(''); // Clear parameter result
        setParameterFlag('normal'); // Reset flag
        setAvailableFlags(['Select/Enter']); // Reset flags
        setEditingIndex(null); // Reset editing index
        // Optionally reload the lab requests to show updated status
        loadPatientLabs(selectedPatient.id, selectedPatient.visitDate);
      }
    } catch (error) {
      console.error('Error saving results:', error);

      // Close loading dialog and show error. The SERVER's reason first — the
      // axios message ("Request failed with status code 500") says nothing
      // about why; the response body carries the actual database error.
      const d = error?.response?.data || {};
      const why = [d.message, d.error].filter(Boolean).join(': ');
      Swal.fire({
        icon: 'error',
        title: 'Error Saving Results',
        text: why || error.message || 'An error occurred while saving the results. Please try again.',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleGenerateResultsWithAi = async () => {
    const nonDerived = testParameters.filter(p => !isDerivedParam(p));
    if (nonDerived.length === 0) return;
    setAiResultsGenerating(true);
    try {
      const { data } = await axios.post(generateLabResults, {
        testName:      selectedTestForResults?.test_name || '',
        parameters:    nonDerived.map(p => ({
          test:     p.test,
          unit:     p.unit     || '',
          male:     p.male     || '',
          female:   p.female   || '',
          children: p.children || '',
          neonates: p.neonates || '',
          flag:     p.flag     || 'normal, high, low',
        })),
        patientAge:    selectedPatient?.age    || 0,
        patientGender: selectedPatient?.gender || '',
        complaint:     selectedPatient?.complaint || '',
      });

      if (!data?.success || !Array.isArray(data.data?.results)) {
        throw new Error(data?.message || 'The AI did not return usable results.');
      }
      if (data.data.results.length === 0) {
        throw new Error('No results returned. Try again.');
      }

      const newResults = data.data.results.map(aiR => {
        const param = nonDerived.find(p => p.test === aiR.parameterName);
        if (!param) return null;
        return {
          parameterName:      param.test,
          result:             aiR.result,
          result_alt:         '',
          unit:               param.unit || '',
          unit_alt:           '',
          entered_unit_kind:  null,
          range:              getParameterRange(param),
          flag:               aiR.flag || 'normal',
        };
      }).filter(Boolean);

      setEnteredResults(prev => {
        const derivedOnly = prev.filter(r => r.derived);
        return [...newResults, ...derivedOnly];
      });

      Swal.fire({
        icon: 'success',
        title: 'AI Results Generated',
        text: `${newResults.length} result${newResults.length === 1 ? '' : 's'} loaded — review and save.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Generation failed.';
      Swal.fire({
        icon: 'error',
        title: 'Generation Failed',
        text: msg,
        confirmButtonColor: '#3085d6',
      });
    } finally {
      setAiResultsGenerating(false);
    }
  };

  const handleEditResult = (index) => {
    const resultToEdit = enteredResults[index];
    setEditingIndex(index);

    // Find the parameter in testParameters to get full details
    const parameter = testParameters.find(param => param.test === resultToEdit.parameterName);
    if (parameter) {
      setSelectedParameter(parameter);
      // Restore the unit the technician originally typed in. If the saved
      // row was alt-side, pre-load the alt value and switch the toggle so
      // the user sees what they actually entered.
      const kind = resultToEdit.entered_unit_kind === 'alt' ? 'alt' : 'primary';
      setParameterUnitKind(kind);
      setParameterResult(kind === 'alt' ? (resultToEdit.result_alt || resultToEdit.result || '') : (resultToEdit.result || ''));
      setParameterFlag(resultToEdit.flag);

      // trans_code will be preserved and sent to backend with update
      if (resultToEdit.trans_code) {
        // trans_code exists - this will trigger UPDATE instead of INSERT
      }

      // Load flags for this parameter
      handleParameterSelect(parameter);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setAvailableFlags(['Select/Enter']);
    setParameterUnitKind('primary');
  };


  const handleRemoveResult = async (resultk) => {
  // Check if result has been saved to database (has trans_code)
  if (!resultk.trans_code) {
    // Result not yet saved to database - just remove from local state
    const updatedResults = enteredResults.filter((r) => r !== resultk);
    setEnteredResults(updatedResults);
    
    Swal.fire({
      icon: 'success',
      title: 'Result Removed',
      text: 'The test result has been removed successfully.',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false
    });
    return;
  }

  // Result exists in database - confirm and delete
  Swal.fire({
    title: 'Remove Result',
    text: 'Are you sure you want to remove this test result? This action cannot be undone.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, Remove',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      // Show loading
      Swal.fire({
        title: 'Deleting Result...',
        text: 'Please wait while we remove the test result.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        // Delete from database
        const response = await axios.delete(deleteResultC(resultk.trans_code));

        if (response.data.success) {
          // Remove from local state
          const updatedResults = enteredResults.filter((r) => r.trans_code !== resultk.trans_code);
          setEnteredResults(updatedResults);

          Swal.fire({
            icon: 'success',
            title: 'Result Removed',
            text: 'The test result has been removed successfully from the database.',
            timer: 2000,
            timerProgressBar: true,
            showConfirmButton: false
          });

          // Optionally reload the patient labs to reflect the change
          if (selectedPatient && selectedTestForResults) {
            await loadExistingResults(selectedTestForResults, testCategory);
          }
        } else {
          throw new Error(response.data.message || 'Failed to delete result');
        }
      } catch (error) {
        console.error('❌ Error deleting result:', error);
        
        Swal.fire({
          icon: 'error',
          title: 'Error Deleting Result',
          text: error.response?.data?.message || error.message || 'Failed to delete the test result. Please try again.',
          confirmButtonColor: '#d33'
        });
      }
    }
  });
};

  // Report Functions
  const handleViewReport = async () => {
    if (!selectedPatient) return;

    setReportLoading(true);
    try {
      const response = await fetch(loadPatientLabReport, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setReportData(data.reportData || {});
        setReportDialogOpen(true);
      }
      setReportLoading(false);
    } catch (error) {
      setReportLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load lab report. Please try again.',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleCloseReport = () => {
    setReportDialogOpen(false);
    setReportData({});
  };

  const handleViewTodayLabResults = async () => {
    try {
      const response = await fetch(loadPatientLabResultsToday, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient?.id
        })
      });

      const data = await response.json();

      if (data.success && data.reportData && Object.keys(data.reportData).length > 0) {
        // Hand off to the shared print dialog so the user can pick a template
        // (and supply signatories for the branded one) before the window opens.
        setPrintReportData(data.reportData);
        setPrintCategoryInterpretations(data.categoryInterpretations || {});
        setPrintReferringDoctor(data.referringDoctor || '');
        setPrintCategoryHistograms(data.categoryHistograms || {});
        setPrintCategoryNarratives(data.categoryNarratives || {});
        setPrintCategoryFormats(data.categoryFormats || {});
        setPrintPatient(selectedPatient);
        setPrintDialogOpen(true);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Results',
          text: 'No lab results found for today.'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load today\'s lab results.'
      });
    }
  };

  const handleViewDocumentation = async () => {
    if (!selectedPatient) {
      Swal.fire({
        icon: 'warning',
        title: 'No Patient Selected',
        text: 'Please select a patient first.',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    setDocumentationLoading(true);
    try {
      const response = await axios.get(loadPatientDocumentation, {
        params: { patientId: selectedPatient.id }
      });

      if (response.data.success && response.data.data) {
        setDocumentationData(response.data.data);
        setDocumentationDialogOpen(true);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Documentation',
          text: 'No documentation records found for this patient.',
          confirmButtonColor: '#1976d2'
        });
      }
    } catch (error) {
      console.error('Error loading documentation:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load patient documentation.',
        confirmButtonColor: '#d32f2f'
      });
    } finally {
      setDocumentationLoading(false);
    }
  };

  const handleCloseDocumentation = () => {
    setDocumentationDialogOpen(false);
    setDocumentationData(null);
  };



  // Clears the current selection and returns to the worklist view.
  const handleBackToWorklist = () => {
    setSelectedPatient(null);
    setPatientLabs([]);
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Page header — toggles between worklist title and patient breadcrumb+back */}
      {selectedPatient ? (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            border: '1px solid', borderColor: 'divider',
            borderRadius: 2.5,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
          }}
        >
          <Button
            onClick={handleBackToWorklist}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#1A9B7F',
              borderRadius: 1.75,
              px: 1.75,
              '&:hover': { bgcolor: 'rgba(26,155,127,0.08)' },
            }}
          >
            Back to Worklist
          </Button>
          <Box sx={{ width: '1px', height: 28, bgcolor: '#e5e9ef' }} />
          <Box sx={{ minWidth: 0, flexShrink: 1 }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 700,
              color: '#1A9B7F', letterSpacing: '1.4px',
              textTransform: 'uppercase', lineHeight: 1,
            }}>
              Patient Investigations
            </Typography>
            <Typography sx={{
              fontSize: { xs: '1rem', md: '1.15rem' },
              fontWeight: 700,
              color: 'text.primary',
              mt: 0.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {selectedPatient.fullName}
              <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary', ml: 1, fontSize: '0.88rem' }}>
                · ID {selectedPatient.id}
              </Box>
            </Typography>
          </Box>

          {/* Quick-jump search: type an ID, hit Enter or Search, load that patient's investigations */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <TextField
              size="small"
              value={quickSearchId}
              onChange={(e) => setQuickSearchId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleQuickSearchById();
                }
              }}
              placeholder="Patient ID…"
              sx={{
                width: { xs: 140, md: 180 },
                '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearchId && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setQuickSearchId('')}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              onClick={handleQuickSearchById}
              disabled={!quickSearchId.trim()}
              variant="contained"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.75,
                bgcolor: '#1A9B7F',
                px: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#15826A', boxShadow: 'none' },
              }}
            >
              Search
            </Button>
          </Box>
        </Paper>
      ) : (
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          My Patients
        </Typography>
      )}

      {/* Patient List — only visible when no patient is selected */}
      {!selectedPatient && (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, md: 2.5 },
          border: '1px solid', borderColor: 'divider',
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography sx={{
            fontSize: '0.72rem', fontWeight: 700,
            color: '#1A9B7F', letterSpacing: '1.4px',
            textTransform: 'uppercase', mb: 0.5,
          }}>
            Investigation Workspace
          </Typography>
          <Typography sx={{
            fontSize: { xs: '1.25rem', md: '1.45rem' },
            fontWeight: 700, color: 'text.primary',
            letterSpacing: '-0.3px', lineHeight: 1.2,
            mb: 0.5,
          }}>
            Patient Worklist
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
            {(() => {
              const n = searchResults.length;
              if (filterLoading && n === 0) return 'Loading patients…';
              if (n === 0) return 'No patients currently have active investigations.';
              const word = n === 1 ? 'patient' : 'patients';
              return `${n} ${word} with active investigations. Click any row to open the chart.`;
            })()}
          </Typography>
        </Box>

        {/* Filter chips + search */}
        <Paper elevation={0} sx={{
          p: 1.5, mb: 2,
          border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper',
        }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
              {[
                { key: 'all',        label: 'All',        count: patientCounts.all,        color: 'text.secondary' },
                { key: 'InPatient',  label: 'Inpatient',  count: patientCounts.InPatient,  color: '#15803D' },
                { key: 'OutPatient', label: 'Outpatient', count: patientCounts.OutPatient, color: '#A16207' },
              ].map(f => {
                const active = filterType === f.key;
                return (
                  <Chip
                    key={f.key}
                    label={`${f.label} (${f.count})`}
                    onClick={() => { setFilterType(f.key); setPatientPage(0); }}
                    size="small"
                    sx={{
                      height: 30,
                      fontWeight: 600, fontSize: '0.78rem',
                      bgcolor: active ? f.color : '#fff',
                      color:   active ? '#fff' : '#334155',
                      border: '1px solid',
                      borderColor: active ? f.color : '#e5e9ef',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: active ? f.color : `${f.color}0D`, borderColor: f.color },
                    }}
                  />
                );
              })}
            </Stack>

            <TextField
              size="small"
              inputRef={searchInputRef}
              placeholder="Search by name, ID, phone or insurance…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPatientPage(0); }}
              sx={{
                width: { xs: '100%', md: 340 },
                '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: 'text.secondary' }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Paper>

        {/* Patient table */}
        <Paper elevation={0} sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper',
        }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' } }}>
                  {[
                    { id: 'fullName',  label: 'Patient',      sortable: true,  width: '28%' },
                    { id: 'age',       label: 'Age / Gender', sortable: true,  width: '14%' },
                    { id: 'state',     label: 'Status',       sortable: true,  width: '12%' },
                    { id: 'insurance', label: 'Insurance',    sortable: true,  width: '18%' },
                    { id: 'visitDate', label: 'Visit Date',   sortable: true,  width: '14%' },
                    { id: 'tel',       label: 'Phone',        sortable: false, width: '12%' },
                    { id: 'actions',   label: '',             sortable: false, width: '2%', align: 'right' },
                  ].map(c => (
                    <TableCell
                      key={c.id}
                      align={c.align || 'left'}
                      sx={{
                        width: c.width,
                        fontWeight: 700, fontSize: '0.72rem',
                        color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px',
                        py: 1.25,
                      }}
                    >
                      {c.sortable ? (
                        <TableSortLabel
                          active={patientOrderBy === c.id}
                          direction={patientOrderBy === c.id ? patientOrder : 'asc'}
                          onClick={() => handlePatientSort(c.id)}
                          sx={{ '& .MuiTableSortLabel-icon': { fontSize: 16 } }}
                        >
                          {c.label}
                        </TableSortLabel>
                      ) : c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filterLoading && searchResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                      <CircularProgress size={28} sx={{ color: '#1A9B7F' }} />
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 1.5 }}>
                        Loading patients…
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pagedPatients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                      <PersonIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        {searchTerm || filterType !== 'all'
                          ? 'No patients match your filters.'
                          : 'No patients to show.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pagedPatients.map((p) => {
                  const state = canonicalState(p.state);
                  const isInpatient = state === 'InPatient';
                  //  Under 12; at 12 the patient is an adult. Same boundary as
                  //  utils/age.js and the prescribing lists.
                  const isChild = (p.age != null) && Number(p.age) < 12;
                  const isSelected = selectedPatient && selectedPatient.id === p.id;
                  return (
                    <TableRow
                      key={p.id}
                      hover
                      onClick={() => handlePatientSelect(p)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'rgba(26,155,127,0.08)' : 'transparent',
                        '& td': { borderBottom: '1px solid #f1f5f9', py: 1.25 },
                        '&:hover .row-arrow': { opacity: 1, transform: 'translateX(2px)' },
                        '&:last-child td': { borderBottom: 'none' },
                      }}
                    >
                      {/* Patient (avatar + name + ID) */}
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{
                            width: 36, height: 36, fontSize: 13, fontWeight: 700,
                            bgcolor: isChild ? '#FCE7F3' : 'rgba(26,155,127,0.12)',
                            color:   isChild ? '#BE185D' : '#1A9B7F',
                          }}>
                            {isChild ? <ChildCareIcon sx={{ fontSize: 18 }} /> : initialsOf(p.fullName)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{
                              fontWeight: 600, fontSize: '0.88rem', color: 'text.primary',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {p.fullName || 'Unknown'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                              ID: {p.id}{p.a_name ? ` · Ref: ${p.a_name}` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Age / Gender */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary' }}>
                          {p.age ?? '—'} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>yrs</Box>
                        </Typography>
                        <Chip
                          label={p.gender || '—'}
                          size="small"
                          sx={{
                            mt: 0.25, height: 18, fontSize: '0.65rem', fontWeight: 700,
                            bgcolor: String(p.gender).toUpperCase() === 'MALE' ? '#EFF6FF' : '#FDF2F8',
                            color:   String(p.gender).toUpperCase() === 'MALE' ? '#1D4ED8' : '#BE185D',
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={state}
                          size="small"
                          sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 700,
                            bgcolor: isInpatient ? '#DCFCE7' : '#FEF3C7',
                            color:   isInpatient ? '#15803D' : '#A16207',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>

                      {/* Insurance */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                          {p.insurance || '—'}
                        </Typography>
                      </TableCell>

                      {/* Visit Date */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                          {p.visitDate
                            ? new Date(p.visitDate).toISOString().slice(0, 10)
                            : '—'}
                        </Typography>
                      </TableCell>

                      {/* Phone */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {p.tel || '—'}
                        </Typography>
                      </TableCell>

                      {/* Action arrow */}
                      <TableCell align="right">
                        <Tooltip title="Open chart" arrow>
                          <IconButton
                            size="small"
                            className="row-arrow"
                            onClick={(e) => { e.stopPropagation(); handlePatientSelect(p); }}
                            sx={{
                              color: '#1A9B7F',
                              opacity: 0.55,
                              transition: 'opacity .15s ease, transform .15s ease',
                              '&:hover': { bgcolor: 'rgba(26,155,127,0.12)', opacity: 1 },
                            }}
                          >
                            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {visiblePatients.length > patientRowsPerPage && (
            <TablePagination
              component="div"
              count={visiblePatients.length}
              page={patientPage}
              onPageChange={(_, p) => setPatientPage(p)}
              rowsPerPage={patientRowsPerPage}
              onRowsPerPageChange={(e) => { setPatientRowsPerPage(parseInt(e.target.value, 10)); setPatientPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #e5e9ef' }}
            />
          )}
        </Paper>
      </Paper>
      )}

      {/* Selected Patient Information */}
     {selectedPatient && (
  <Card
    sx={{
      mb: 3,
      borderRadius: 4,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
        zIndex: 1
      }
    }}
  >
    <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
      <Grid container spacing={2} wrap="nowrap">
        {/* Patient Profile Section */}
        <Grid item xs={2.4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #0fd34aff 0%, #319249ff 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                right: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                mb: 2,
                fontSize: '2.5rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(10px)',
                zIndex: 1
              }}
            >
              {selectedPatient.fullName?.split(" ").map(n => n[0]).join("").slice(0, 2) || 'P'}
            </Avatar>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 1 }}>
              {selectedPatient.fullName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, opacity: 0.95, zIndex: 1 }}>
              {selectedPatient.age} years old
            </Typography>
            <Box
              sx={{
                px: 2.5,
                py: 0.75,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                mb: 2,
                zIndex: 1
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                ID: {selectedPatient.id}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2, zIndex: 1, flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
              <Chip
                label={selectedPatient.gender}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              />
              <Chip
                label={selectedPatient.insurance}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              />
            </Stack>
            <Chip
              label={selectedPatient.state}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                color: 'white',
                fontWeight: 600,
                px: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(10px)',
                zIndex: 1
              }}
            />
          </Box>
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#f8f9fd',
              border: '1px solid #e8eaf6',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  mr: 2
                }}
              >
                <PhoneIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary">
                Contact Info
              </Typography>
            </Box>

            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Phone Number
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                  {selectedPatient.tel || 'Not provided'}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Location
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                  {selectedPatient.res || 'Not provided'}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Visit Date
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, color: 'primary.main' }}>
                  {selectedPatient.visitDate}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Vital Signs Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#fff3e0',
              border: '1px solid #ffe0b2',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(255, 152, 0, 0.15)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  mr: 2
                }}
              >
                <HospitalIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#e65100' }}>
                Vital Signs
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Temperature
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.temperature || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Blood Pressure
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.bp || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Pulse
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.pulse || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Respiratory Rate
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.respiration || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Additional Vitals Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#e8f5e9',
              border: '1px solid #c8e6c9',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(76, 175, 80, 0.15)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  mr: 2
                }}
              >
                <ScienceIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>
                Measurements
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Weight
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.weight || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(76, 175, 80, 0.15)',
                    color: '#2e7d32'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Height
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.height || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(76, 175, 80, 0.15)',
                    color: '#2e7d32'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  BMI
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.bmi || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(76, 175, 80, 0.15)',
                    color: '#2e7d32'
                  }}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Actions Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#f3e8ff',
              border: '1px solid #ddd6fe',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(124, 58, 237, 0.18)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                  mr: 2
                }}
              >
                <ActionsIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#5b21b6' }}>
                Actions
              </Typography>
            </Box>

            <Stack spacing={1.25}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={handleViewTodayLabResults}
                disabled={patientLabs.length === 0}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  backgroundColor: '#43a047',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#388e3c', boxShadow: '0 4px 12px rgba(67,160,71,0.25)' }
                }}
              >
                Today's Results
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={handleViewReport}
                disabled={reportLoading || patientLabs.length === 0}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  backgroundColor: '#1976d2',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#1565c0', boxShadow: '0 4px 12px rgba(25,118,210,0.25)' }
                }}
              >
                {reportLoading ? <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} /> : null}
                View Report
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => handleViewDocumentation()}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  borderColor: '#ff9800', color: '#ff9800',
                  '&:hover': {
                    borderColor: '#f57c00',
                    backgroundColor: 'rgba(255, 152, 0, 0.06)'
                  }
                }}
              >
                View Documentation
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => setMedicalHistoryOpen(true)}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  borderColor: '#7c3aed', color: '#7c3aed',
                  '&:hover': {
                    borderColor: '#5b21b6',
                    backgroundColor: 'rgba(124, 58, 237, 0.06)'
                  }
                }}
              >
                Medical History
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
)}

      {/* Requested Labs Section */}
      {selectedPatient && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Requested Laboratory Tests
              </Typography>
              <Chip
                label={`${patientLabs.length} test${patientLabs.length === 1 ? '' : 's'}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>

           {loading ? (
  <Box display="flex" justifyContent="center" p={3}>
    <CircularProgress />
  </Box>
) : patientLabs.length === 0 ? (
  // The old text here blamed "active time windows" unconditionally. That is
  // only one of three reasons the list can be empty, and it is the least
  // likely — so it sent people to the wrong setting. Name the real cause.
  unpaidVisibility === 'hide' ? (
    <Alert severity="warning">
      <strong>Unpaid orders are hidden.</strong> This patient may well have lab
      requests — they are being filtered out because they have not been paid
      for. Change this under{' '}
      <strong>Admin Panel → System Settings → Unpaid Lab &amp; Scan Visibility</strong>{' '}
      (note: that is a different card from “Lab &amp; Scan Stock Control”, which
      governs reagents, not payment).
    </Alert>
  ) : (
    <Alert severity="info">
      No laboratory tests found for this patient. Requests older than 14 days are
      not listed here — check Lab History for those.
    </Alert>
  )
) : (
  <>
    {/* One notice for the whole list, so the technician knows the rule before
        reaching for a button — not after filling in a result set. Only shown
        when it actually applies: the policy blocks unpaid entry AND at least
        one row on this patient is unpaid. */}
    {!canServeUnpaid && patientLabs.some(l => l.isPaid === false) && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>
          {patientLabs.filter(l => l.isPaid === false).length} of {patientLabs.length}{' '}
          {patientLabs.filter(l => l.isPaid === false).length === 1 ? 'test is' : 'tests are'} unpaid.
        </strong>{' '}
        Results cannot be entered for those until payment is recorded — their
        button reads <em>Awaiting Payment</em>. The paid ones can be entered as
        normal.
        <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.85em' }}>
          To allow entry before payment, an administrator can set{' '}
          <strong>System Settings → Billing &amp; Authorization → Lab &amp; Scan —
          PAYMENT gate</strong> to “Show unpaid orders and allow results”.
        </Box>
      </Alert>
    )}
    <List>
      {patientLabs
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((lab, index) => (
        <React.Fragment key={`${lab.patient_id || lab.id}_${lab.test_name}_${lab.requested_date || index}`}>
          <ListItemButton
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1" fontWeight={600}>
                    {lab.test_name} {lab.category && `(${lab.category})`}
                  </Typography>
                  
                  {/* Show the entry window period */}
                  {lab.time_period && (
                    <Chip 
                      label={`${lab.time_period} day${lab.time_period > 1 ? 's' : ''} window`}
                      size="small"
                      sx={{ 
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  
                  {/* Show remaining hours for entry with color coding */}
                  {lab.entry_status === 'active' && 
                   lab.hours_since_request !== undefined && 
                   lab.allowed_hours !== undefined && (
                    <Chip 
                      label={`${Math.max(0, Math.round(lab.allowed_hours - lab.hours_since_request))}h left`}
                      size="small"
                      color={
                        (lab.allowed_hours - lab.hours_since_request) < 6 ? 'error' :      // Red if < 6 hours
                        (lab.allowed_hours - lab.hours_since_request) < 12 ? 'warning' :   // Orange if < 12 hours
                        'success'                                                           // Green otherwise
                      }
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Lab #: {lab.lab_no} • Requested: {lab.requested_date}
                    {lab.requested_time && ` at ${lab.requested_time}`} • By: {lab.requ_by}
                  </Typography>
                  {lab.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      Notes: {lab.notes}
                    </Typography>
                  )}
                  {/* Progress tracker — chip shows the current stage; the
                      button moves it forward one step (Sample Taken →
                      Processing → Done). "Done" is set automatically when
                      results are saved, so the button vanishes from there. */}
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <ProgressChip status={lab.progress_status} />
                    <AdvanceProgressButton
                      reqNo={lab.req_no}
                      status={lab.progress_status}
                      onAdvanced={(toStatus) => {
                        // Patch the lab item in place so the chip updates
                        // without a full reload of the patient's worklist.
                        setPatientLabs(prev => prev.map(item =>
                          item === lab ? { ...item, progress_status: toStatus } : item
                        ));
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PrintLabelIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        printSampleLabels([{
                          patientName: selectedPatient.fullName,
                          patientId:   selectedPatient.id,
                          date:        lab.requested_date || new Date().toISOString().slice(0, 10),
                          testName:    lab.test_name,
                          sampleId:    lab.lab_no,
                        }]);
                      }}
                      sx={{
                        py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0,
                        textTransform: 'none', borderColor: '#11998e', color: '#11998e',
                        '&:hover': { borderColor: '#0e8679', bgcolor: 'rgba(17,153,142,0.06)' },
                      }}
                    >
                      Print Label
                    </Button>
                  </Box>
                </Box>
              }
            />
            <Box display="flex" flexDirection="column" gap={2} sx={{ p: 2, ml: 2, alignItems: 'center' }}>
              {/* Payment gate, shown rather than discovered.
                  The server refuses result entry for an unpaid order unless the
                  policy is 'serve' — it always did, and always will, since that
                  is the enforcement. But letting the technician fill in a whole
                  result set and THEN rejecting it wastes their work and reads
                  like a fault. So when the same rule that will refuse the save
                  is already known, the button is disabled up-front and says why.
                  `lab.isPaid` and `canServeUnpaid` both come from the server, so
                  this can never disagree with what the endpoint will do. */}
              {(() => {
                const blockedForPayment = lab.isPaid === false && !canServeUnpaid;
                return (
                  <Tooltip
                    title={blockedForPayment
                      ? 'Payment has not been recorded for this test. Results cannot be entered until it is paid, or until an administrator changes Unpaid Lab & Scan Visibility to “Show unpaid orders and allow results”.'
                      : ''}
                    arrow
                    disableHoverListener={!blockedForPayment}
                  >
                    {/* Tooltip needs a non-disabled child to receive events, so
                        the disabled Button is wrapped. */}
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={blockedForPayment}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEnterResults(lab);
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 3,
                          py: 1,
                          background: blockedForPayment
                            ? undefined
                            : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          '&:hover': {
                            background: blockedForPayment
                              ? undefined
                              : 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                          }
                        }}
                      >
                        {blockedForPayment ? 'Awaiting Payment' : 'Enter Results'}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
            </Box>
          </ListItemButton>
          {index < Math.min(rowsPerPage, patientLabs.length - page * rowsPerPage) - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>

    {/* General sample-tube label printer. One physical blood draw often
        covers several tests that share a lab number, so we print one
        master label per unique lab_no in the worklist. Hidden when the
        patient only has a single test — the per-row button already
        covers that case. */}
    {patientLabs.length > 1 && (() => {
      const seen = new Set();
      const groups = [];
      for (const l of patientLabs) {
        if (!l.lab_no || seen.has(l.lab_no)) continue;
        seen.add(l.lab_no);
        groups.push(l);
      }
      return (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintLabelIcon />}
            onClick={() => {
              const labels = groups.map(l => ({
                patientName: selectedPatient.fullName,
                patientId:   selectedPatient.id,
                date:        l.requested_date || new Date().toISOString().slice(0, 10),
                sampleId:    l.lab_no,
              }));
              printSampleLabels(labels);
            }}
            sx={{
              textTransform: 'none', fontWeight: 600,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)' },
            }}
          >
            Print General Sample Label{groups.length > 1 ? `s (${groups.length})` : ''}
          </Button>
        </Box>
      );
    })()}

    {patientLabs.length > rowsPerPage && (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, patientLabs.length)} of {patientLabs.length} tests
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              variant="outlined"
              size="small"
            >
              Previous
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                Page {page + 1} of {Math.ceil(patientLabs.length / rowsPerPage)}
              </Typography>
            </Box>
            <Button
              disabled={page >= Math.ceil(patientLabs.length / rowsPerPage) - 1}
              onClick={() => setPage(page + 1)}
              variant="outlined"
              size="small"
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Box>
    )}
  </>
)}
          </CardContent>
        </Card>
      )}


      {/* Results Entry Dialog */}
    <Dialog
      open={resultsDialogOpen}
      onClose={() => setResultsDialogOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '92vh',
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: t.pageBg,
          backgroundImage: 'none',
          border: t.isDark ? `1px solid ${t.borderSubtle}` : 'none',
          boxShadow: t.isDark
            ? '0 24px 64px rgba(0,0,0,0.7)'
            : '0 24px 64px rgba(15,23,42,0.18)',
        }
      }}
    >
      <DialogTitle sx={{
        p: 0,
        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.78)} 100%)`,
        color: 'white',
        position: 'relative',
        '&::after': {
          content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.14), transparent 60%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.75,
          px: 3, py: 2.25,
          position: 'relative', zIndex: 1,
        }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 1.75,
            backgroundColor: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}>
            <ScienceIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
              Enter Laboratory Results
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', opacity: 0.9 }}>
              {selectedTestForResults?.test_name}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setResultsDialogOpen(false)}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.18)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: t.pageBg, p: { xs: 2, md: 2.5 } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2.25 }}>

          {/* Narrative entry — used for descriptive labs like Peripheral
              Blood Smear Examination. Each parameter row from the catalog
              becomes a section title with a multi-line text area; below
              the sections there's a single "Impression" text area for the
              clinical conclusion. No parameter dropdowns / unit toggles /
              flag chips — the report template renders the saved text
              verbatim. */}
          {testLabFormat === 'narrative' ? (
            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: `1px solid ${t.borderSubtle}`, backgroundColor: t.surface }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${t.accent.teal} 0%, #38ef7d 100%)`, color: 'white', fontWeight: 800, fontSize: '0.85rem' }}>
                  N
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: t.text }}>
                    Narrative result entry
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: t.textMuted }}>
                    {testCategory} · type one paragraph per section, then the clinical Impression.
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${testParameters.length} section${testParameters.length === 1 ? '' : 's'}`}
                  sx={{ fontWeight: 700, fontSize: '0.7rem', backgroundColor: alpha(t.accent.teal, t.isDark ? 0.18 : 0.12), color: t.accent.teal, border: `1px solid ${alpha(t.accent.teal, 0.3)}` }}
                />
              </Stack>

              {loadingParameters ? (
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 220 }}>
                  <CircularProgress sx={{ color: t.accent.teal }} />
                </Box>
              ) : testParameters.length === 0 ? (
                <Alert severity="info">
                  This lab has no sections defined. Set them up in Setup Lab (Result format → Narrative).
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {testParameters.map((p) => (
                    <Box key={p.test}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: t.text, mb: 0.5 }}>
                        {p.test}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder={`Describe findings for ${p.test}…`}
                        value={narrativeSections[p.test] || ''}
                        onChange={(e) => setNarrativeSections((prev) => ({ ...prev, [p.test]: e.target.value }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: t.fieldBg,
                            fontSize: '0.88rem',
                          },
                        }}
                      />
                    </Box>
                  ))}

                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: t.text, mt: 1, mb: 0.5, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                      Impression
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Clinical conclusion — e.g. Findings are consistent with Chronic Myeloid Leukemia."
                      value={narrativeImpression}
                      onChange={(e) => setNarrativeImpression(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: t.fieldBg,
                          fontSize: '0.88rem',
                        },
                      }}
                    />
                  </Box>
                </Stack>
              )}
            </Paper>
          ) : (
          <>

          {/* Sections 1 & 2: Parameters List and Entry Form Side by Side */}
          <Grid container spacing={2.25} columns={{ xs: 4, sm: 8, md: 12 }}>
            {/* Section 1: Parameters List (Left Side) */}
            <Grid item xs={12} md={6} size={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.25,
                  borderRadius: 2,
                  border: `1px solid ${t.borderSubtle}`,
                  backgroundColor: t.surface,
                  backgroundImage: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.25,
                    background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 800, fontSize: '0.95rem',
                    boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.32)}`
                  }}>
                    1
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 700, fontSize: '0.95rem', color: t.text,
                      lineHeight: 1.2, letterSpacing: '-0.01em'
                    }}>
                      Select Parameter
                    </Typography>
                    {testCategory && (
                      <Typography sx={{ fontSize: '0.78rem', color: t.textMuted, mt: 0.15 }}>
                        Category: {testCategory}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    size="small"
                    label={`${testParameters.length} param${testParameters.length === 1 ? '' : 's'}`}
                    sx={{
                      fontWeight: 700, fontSize: '0.7rem',
                      backgroundColor: alpha(t.accent.teal, t.isDark ? 0.18 : 0.12),
                      color: t.accent.teal,
                      border: `1px solid ${alpha(t.accent.teal, 0.3)}`
                    }}
                  />
                </Box>

                {loadingParameters ? (
                  <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: { xs: 280, md: 360 } }}>
                    <CircularProgress sx={{ color: t.accent.teal }} />
                  </Box>
                ) : (
                  /* Fixed-height scroll container — caps the parameter list
                     so a 30-parameter test never pushes the right-side
                     entry form (or the Added Results table below) out of
                     view. The list scrolls internally, the dialog stays in
                     the viewport. */
                  <Box sx={{
                    height: { xs: 280, md: 360 },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: `1px solid ${t.borderSubtle}`,
                    borderRadius: 1.5,
                    backgroundColor: t.surfaceAlt,
                    // Nicer scrollbar that respects the theme.
                    '&::-webkit-scrollbar': { width: 8 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: alpha(t.text, t.isDark ? 0.18 : 0.20),
                      borderRadius: 4,
                      '&:hover': { backgroundColor: alpha(t.text, t.isDark ? 0.30 : 0.35) }
                    }
                  }}>
                    <List dense disablePadding>
                      {testParameters.map((param, index) => {
                        const selected = selectedParameter?.test === param.test;
                        return (
                          <ListItemButton
                            key={index}
                            selected={selected}
                            onClick={() => handleParameterSelect(param)}
                            disabled={editingIndex !== null}
                            sx={{
                              borderBottom: `1px solid ${t.borderSubtle}`,
                              '&:last-child': { borderBottom: 0 },
                              transition: 'all 0.15s ease',
                              '&.Mui-selected': {
                                background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                                color: 'white',
                                '& .MuiListItemText-primary, & .MuiListItemText-secondary': { color: 'inherit' },
                                '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.85)' },
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.72)} 100%)`
                                }
                              },
                              '&.Mui-disabled': { opacity: 0.5 },
                              '&:hover': { backgroundColor: alpha(t.accent.teal, 0.06) }
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: selected ? 'white' : t.text }}>
                                    {param.test}
                                  </Typography>
                                  {isDerivedParam(param) && (
                                    <Chip
                                      label="Σ auto"
                                      size="small"
                                      sx={{
                                        height: 18, fontSize: '0.62rem', fontWeight: 800,
                                        bgcolor: selected ? 'rgba(255,255,255,0.20)' : 'rgba(99,102,241,0.18)',
                                        color: selected ? 'white' : '#a5b4fc',
                                        border: selected ? '1px solid rgba(255,255,255,0.40)' : '1px solid rgba(165,180,252,0.35)',
                                        '& .MuiChip-label': { px: 0.7 },
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography sx={{ fontSize: '0.74rem', color: selected ? 'rgba(255,255,255,0.85)' : t.textMuted }}>
                                  {isDerivedParam(param)
                                    ? <>Auto-computed: <code>{param.formula}</code></>
                                    : <>Category: {param.cat}</>}
                                </Typography>
                              }
                            />
                            {loadingParameterDetails && selected && (
                              <CircularProgress size={18} sx={{ ml: 1, color: 'white' }} />
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Section 2: Parameter Entry Form (Right Side) */}
            <Grid item xs={12} md={6} size={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.25,
                  borderRadius: 2,
                  border: `1px solid ${t.borderSubtle}`,
                  backgroundColor: t.surface,
                  backgroundImage: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.25,
                    background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 800, fontSize: '0.95rem',
                    boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.32)}`
                  }}>
                    2
                  </Box>
                  <Typography sx={{
                    fontWeight: 700, fontSize: '0.95rem', color: t.text,
                    letterSpacing: '-0.01em'
                  }}>
                    Enter Result
                  </Typography>
                  {selectedParameter && (
                    <Chip
                      size="small"
                      label={selectedParameter.test}
                      sx={{
                        ml: 'auto',
                        fontWeight: 700, fontSize: '0.7rem',
                        maxWidth: 220,
                        backgroundColor: alpha(t.accent.green, t.isDark ? 0.18 : 0.12),
                        color: t.accent.green,
                        border: `1px solid ${alpha(t.accent.green, 0.3)}`,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                      }}
                    />
                  )}
                </Box>

                <Stack spacing={1.75}>
                  <TextField
                    fullWidth
                    label="Selected Parameter"
                    value={selectedParameter?.test || ''}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: t.fieldBg,
                        borderRadius: 1.5,
                        '& fieldset': { borderColor: t.borderSubtle }
                      }
                    }}
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <TextField
                      fullWidth
                      label="Unit"
                      value={selectedParameter?.unit || ''}
                      onChange={(e) => {
                        if (selectedParameter) {
                          setSelectedParameter({ ...selectedParameter, unit: e.target.value });
                        }
                      }}
                      size="small"
                      placeholder="g/L"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: t.fieldBg, borderRadius: 1.5,
                          '& fieldset': { borderColor: t.borderSubtle },
                          '&.Mui-focused fieldset': { borderColor: t.accent.teal, borderWidth: 2 }
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Reference Range"
                      value={selectedParameter ? getParameterRange(selectedParameter) : ''}
                      onChange={(e) => {
                        if (selectedParameter) {
                          const patientGender = selectedPatient?.gender?.toUpperCase();
                          const updatedParameter = { ...selectedParameter };
                          if (patientGender === 'MALE') updatedParameter.male = e.target.value;
                          else if (patientGender === 'FEMALE') updatedParameter.female = e.target.value;
                          else updatedParameter.children = e.target.value;
                          setSelectedParameter(updatedParameter);
                        }
                      }}
                      size="small"
                      placeholder="37-53"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: t.fieldBg, borderRadius: 1.5,
                          '& fieldset': { borderColor: t.borderSubtle },
                          '&.Mui-focused fieldset': { borderColor: t.accent.teal, borderWidth: 2 }
                        }
                      }}
                    />
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel>Flag</InputLabel>
                    <Select
                      value={parameterFlag}
                      label="Flag"
                      onChange={(e) => handleFlagChange(e.target.value)}
                      sx={{
                        backgroundColor: t.fieldBg, borderRadius: 1.5,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: t.borderSubtle },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.accent.teal, borderWidth: 2 }
                      }}
                    >
                      {availableFlags.map((flag) => (
                        <MenuItem key={flag} value={flag}>
                          {flag === 'Select/Enter' ? flag : flag.charAt(0).toUpperCase() + flag.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Result Value — multiline textarea capped at 8 rows.
                      Numeric results sit fine on one line at the top;
                      paragraphs grow the field up to 8 rows then scroll
                      inside the textarea, so the dialog never expands
                      beyond the viewport. */}
                  {/* Result Value — branches based on whether the selected
                      parameter is derived. If derived, render a read-only
                      display showing the computed value (or "pending: needs
                      X" when an upstream input is missing) plus the
                      formula. Otherwise the usual multiline textarea. */}
                  {isDerivedParam(selectedParameter) ? (() => {
                    const pending  = derivedPendingInputs(selectedParameter);
                    const computed = derivedComputedValue(selectedParameter);
                    const ready    = pending.length === 0 && computed !== '';
                    return (
                      <Box sx={{
                        p: 1.5, borderRadius: 1.5,
                        bgcolor: ready
                          ? 'rgba(56,239,125,0.10)'
                          : 'rgba(245,158,11,0.10)',
                        border: '1.5px solid',
                        borderColor: ready
                          ? 'rgba(56,239,125,0.40)'
                          : 'rgba(245,158,11,0.40)',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box sx={{
                            fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                            letterSpacing: '0.6px',
                            color: ready ? '#4ade80' : '#f59e0b',
                          }}>
                            {ready ? '✓ Auto-computed' : `Pending — needs ${pending.join(', ')}`}
                          </Box>
                        </Box>
                        <Box sx={{
                          fontSize: '1.4rem', fontWeight: 800, color: t.text,
                          letterSpacing: '-0.02em', lineHeight: 1.1,
                        }}>
                          {ready ? `${computed} ${selectedParameter.unit || ''}` : '—'}
                        </Box>
                        <Box sx={{
                          mt: 1, fontSize: '0.78rem', color: t.textMuted,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        }}>
                          Formula: {selectedParameter.formula}
                        </Box>
                        <Box sx={{ mt: 1.25, fontSize: '0.74rem', color: t.textMuted }}>
                          Derived parameters can't be edited by hand — fill in their inputs and the value appears in the Added Results table automatically.
                        </Box>
                      </Box>
                    );
                  })() : (
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      label={`Result Value${selectedParameter
                        ? ` (in ${parameterUnitKind === 'alt' ? (selectedParameter.alt_unit || '') : (selectedParameter.unit || '—')})`
                        : ''}`}
                      value={parameterResult}
                      onChange={(e) => handleResultChange(e.target.value)}
                      autoFocus
                      placeholder="Enter the result. Numeric values, paragraphs, or microscopy descriptions are all accepted."
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: t.fieldBg,
                          borderRadius: 1.5,
                          fontWeight: 700,
                          fontSize: '1.02rem',
                          p: 1.25,
                          alignItems: 'flex-start',
                          '& fieldset': { borderColor: t.borderSubtle },
                          '&.Mui-focused fieldset': { borderColor: t.accent.teal, borderWidth: 2 }
                        },
                        '& .MuiInputBase-inputMultiline': {
                          lineHeight: 1.5,
                        }
                      }}
                    />
                  )}

                  {/* Dual-unit toggle + live preview — only when the parameter
                      has a configured alt unit. The toggle re-bases the
                      *displayed* unit and converts the currently-typed value
                      so the technician can flip between mg/dL and mmol/L on
                      the fly. The saved row always carries both values. */}
                  {hasDualUnit(selectedParameter) && (() => {
                    const factor = Number(selectedParameter.unit_factor);
                    const primaryUnit = selectedParameter.unit || '';
                    const altUnit     = selectedParameter.alt_unit || '';
                    const num = Number(parameterResult);
                    const numValid = parameterResult !== '' && Number.isFinite(num);
                    const otherValue = numValid
                      ? (parameterUnitKind === 'alt'
                          ? formatConverted(num / factor, parameterResult)
                          : formatConverted(num * factor, parameterResult))
                      : '';
                    const otherUnit = parameterUnitKind === 'alt' ? primaryUnit : altUnit;

                    const switchKind = (kind) => {
                      if (kind === parameterUnitKind) return;
                      // Re-express the typed value into the new unit so the
                      // input doesn't visually jump from 120 → "120 mmol/L".
                      if (numValid) {
                        const converted = kind === 'alt'
                          ? formatConverted(num * factor, parameterResult)
                          : formatConverted(num / factor, parameterResult);
                        setParameterResult(converted);
                      }
                      setParameterUnitKind(kind);
                    };

                    return (
                      <Box sx={{ mt: 1.25 }}>
                        <Box sx={{
                          display: 'inline-flex', p: 0.25, borderRadius: 1.5,
                          bgcolor: 'action.hover',
                          border: '1px solid', borderColor: 'divider',
                          gap: 0.25,
                        }}>
                          {[{ k: 'primary', label: primaryUnit || '—' }, { k: 'alt', label: altUnit || '—' }].map(opt => {
                            const active = parameterUnitKind === opt.k;
                            return (
                              <Box
                                key={opt.k}
                                onClick={() => switchKind(opt.k)}
                                sx={{
                                  px: 1.5, py: 0.4,
                                  borderRadius: 1.25,
                                  fontSize: '0.78rem', fontWeight: 700,
                                  cursor: 'pointer',
                                  color: active ? 'white' : 'text.secondary',
                                  background: active
                                    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                                    : 'transparent',
                                  boxShadow: active ? '0 2px 8px rgba(17,153,142,0.32)' : 'none',
                                  transition: 'all 0.15s ease',
                                  '&:hover': { color: active ? 'white' : 'text.primary' },
                                }}
                              >
                                {opt.label}
                              </Box>
                            );
                          })}
                        </Box>

                        {numValid && otherValue && (
                          <Box sx={{
                            mt: 1, px: 1.25, py: 0.75, borderRadius: 1.5,
                            bgcolor: 'rgba(17,153,142,0.10)',
                            border: '1px solid rgba(56,239,125,0.30)',
                            fontSize: '0.8rem', color: 'text.primary',
                            display: 'flex', alignItems: 'center', gap: 1,
                          }}>
                            <Box sx={{ fontWeight: 800, color: '#4ade80', fontSize: '0.7rem', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                              ↔ Equivalent
                            </Box>
                            <Box sx={{ fontWeight: 700 }}>
                              {otherValue} {otherUnit}
                            </Box>
                            <Box sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.72rem' }}>
                              factor × {factor}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}

                  {/* ── Live Range Verdict ────────────────────────────────────
                      Shows the technician exactly what the auto-detector saw
                      and what status it picked. Renders only when there's a
                      reference range AND a typed numeric result, so it stays
                      out of the way for descriptive results / qualitative
                      parameters. The dropdown above remains the single source
                      of truth — this is a read-out, not an additional input.
                  ─────────────────────────────────────────────────────────── */}
                  {selectedParameter && !isDerivedParam(selectedParameter) && parameterResult && parameterResult.trim() !== '' && (() => {
                    const range = getParameterRange(selectedParameter);
                    if (!range || range === 'Not specified') return null;

                    // Extract the numeric result (handles values like "7", "7.0", "7 mg/dL").
                    const numeric = (function () {
                      const m = String(parameterResult).match(/-?\d+(?:\.\d+)?/);
                      return m ? parseFloat(m[0]) : NaN;
                    })();
                    if (!Number.isFinite(numeric)) return null;

                    const canonical = detectFlagFromResult(parameterResult, range);
                    if (canonical === 'Select/Enter') return null;

                    // Friendly comparator the user sees ("7.0 ≤ 7.0 ≤ 7.0"-ish).
                    // Pull the cleaned bounds from the normalised range so we
                    // can render an explanation that matches what the parser
                    // actually used.
                    const rangeText = String(range)
                      .replace(/[\u2013\u2014\u2212]/g, '-')
                      .replace(/\s+to\s+/gi, ' - ')
                      .trim();

                    const palette = {
                      normal:   { tint: 'rgba(21,128,61,0.14)',  border: 'rgba(74,222,128,0.40)', text: '#4ade80', label: 'Normal',   blurb: 'Within reference range.' },
                      low:      { tint: 'rgba(25,118,210,0.14)', border: 'rgba(96,165,250,0.40)', text: '#60a5fa', label: 'Low',      blurb: 'Below the reference range.' },
                      high:     { tint: 'rgba(220,38,38,0.14)',  border: 'rgba(248,113,113,0.40)', text: '#f87171', label: 'High',     blurb: 'Above the reference range.' },
                      abnormal: { tint: 'rgba(217,119,6,0.14)',  border: 'rgba(251,191,36,0.40)', text: '#fbbf24', label: 'Abnormal', blurb: 'Outside the expected reference.' },
                    }[canonical] || { tint: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', text: 'inherit', label: canonical, blurb: '' };

                    const resolvedFlag = pickAvailableFlag(canonical, availableFlags);

                    return (
                      <Box sx={{
                        mt: 1.25, p: 1.25, borderRadius: 1.5,
                        bgcolor: palette.tint,
                        border: '1px solid', borderColor: palette.border,
                        display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
                      }}>
                        <Box sx={{
                          fontSize: '0.66rem', fontWeight: 800,
                          letterSpacing: '0.6px', textTransform: 'uppercase',
                          color: palette.text,
                        }}>
                          ⚡ Auto Status
                        </Box>
                        <Box sx={{
                          px: 1, py: 0.25, borderRadius: 1,
                          bgcolor: palette.text, color: '#0b0b0b',
                          fontWeight: 800, fontSize: '0.78rem',
                          letterSpacing: '0.3px',
                        }}>
                          {palette.label.toUpperCase()}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', fontSize: '0.78rem', color: 'text.primary' }}>
                          <Box component="span" sx={{ color: 'text.secondary' }}>You entered</Box>
                          <Box component="span" sx={{ fontWeight: 800 }}>{numeric}</Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>vs ref</Box>
                          <Box component="span" sx={{ fontWeight: 800 }}>{rangeText}</Box>
                        </Box>
                        <Box sx={{ ml: { sm: 'auto' }, fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic' }}>
                          {palette.blurb}
                          {resolvedFlag !== canonical && (
                            <> · saved as <Box component="span" sx={{ fontWeight: 700, fontStyle: 'normal' }}>{resolvedFlag}</Box></>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}

                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleAddResult}
                      disabled={!selectedParameter || isDerivedParam(selectedParameter) || !parameterResult.trim()}
                      fullWidth
                      sx={{
                        py: 1.25,
                        borderRadius: 1.5,
                        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        boxShadow: `0 6px 16px ${alpha(t.accent.teal, 0.35)}`,
                        '&:hover': {
                          boxShadow: `0 8px 22px ${alpha(t.accent.teal, 0.45)}`
                        },
                        '&.Mui-disabled': {
                          background: alpha(t.text, 0.08),
                          color: t.textDim,
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {editingIndex !== null ? '✓ Update Result' : '+ Add Result'}
                    </Button>
                    {editingIndex !== null ? (
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        fullWidth
                        sx={{
                          py: 1,
                          borderRadius: 1.5,
                          borderColor: alpha(t.accent.teal, 0.4),
                          color: t.accent.teal,
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: t.accent.teal,
                            backgroundColor: alpha(t.accent.teal, 0.06)
                          }
                        }}
                      >
                        Cancel Edit
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={handleClearResult}
                        fullWidth
                        sx={{
                          py: 1,
                          borderRadius: 1.5,
                          borderColor: t.borderStrong,
                          color: t.text,
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: t.borderStrong,
                            backgroundColor: t.rowHover
                          }
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Section 3: Results Table */}
          <Paper
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: 2,
              border: `1px solid ${t.borderSubtle}`,
              backgroundColor: t.surface,
              backgroundImage: 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.25,
                background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: 800, fontSize: '0.95rem',
                boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.32)}`
              }}>
                3
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: t.text, letterSpacing: '-0.01em' }}>
                  Added Results
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: t.textMuted }}>
                  {enteredResults.length} result{enteredResults.length === 1 ? '' : 's'} added
                </Typography>
              </Box>
              {enteredResults.length > 0 && (
                <Chip
                  size="small"
                  label={`${enteredResults.length} ready`}
                  sx={{
                    fontWeight: 700, fontSize: '0.7rem',
                    backgroundColor: alpha(t.accent.green, t.isDark ? 0.18 : 0.14),
                    color: t.accent.green,
                    border: `1px solid ${alpha(t.accent.green, 0.3)}`
                  }}
                />
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={handleGenerateResultsWithAi}
                disabled={aiResultsGenerating || testParameters.filter(p => !isDerivedParam(p)).length === 0}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
                  borderRadius: 1.25, px: 1.5, py: 0.4,
                  borderColor: alpha(t.accent.teal, 0.5),
                  color: t.accent.teal,
                  '&:hover': { borderColor: t.accent.teal, backgroundColor: alpha(t.accent.teal, 0.06) },
                  '&.Mui-disabled': { opacity: 0.4 },
                }}
              >
                {aiResultsGenerating
                  ? <><CircularProgress size={12} sx={{ mr: 0.5 }} />Generating...</>
                  : '✨ Generate with AI'}
              </Button>
            </Box>

            <TableContainer
              elevation={0}
              sx={{
                maxHeight: 300,
                border: `1px solid ${t.borderSubtle}`,
                borderRadius: 1.5,
                backgroundColor: t.surfaceAlt,
                backgroundImage: 'none',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Parameter', 'Result', 'Unit', 'Range', 'Status', 'Scale', 'Actions'].map(h => (
                      <TableCell key={h} sx={{
                        fontWeight: 700, color: t.text,
                        fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5,
                        backgroundColor: t.surfaceAlt,
                        borderBottom: `1px solid ${t.borderSubtle}`
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enteredResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, borderColor: t.borderSubtle }}>
                        <Typography sx={{ color: t.textMuted, fontSize: '0.88rem' }}>
                          No results added yet. Add your first result above.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    enteredResults.map((result, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '& td': { color: t.text, borderColor: t.borderSubtle },
                          '&:hover': { backgroundColor: t.rowHover }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {result.parameterName}
                            {result.derived && (
                              <Chip
                                label="Σ"
                                size="small"
                                title={`Auto-computed: ${result.formula || ''}`}
                                sx={{
                                  height: 18, fontSize: '0.62rem', fontWeight: 800,
                                  bgcolor: 'rgba(99,102,241,0.18)',
                                  color: '#a5b4fc',
                                  border: '1px solid rgba(165,180,252,0.35)',
                                  '& .MuiChip-label': { px: 0.7 },
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color: result.flag === 'normal'
                                ? '#2e7d32'
                                : result.flag === 'high'
                                ? '#d32f2f'
                                : result.flag === 'low'
                                ? '#ed6c02'
                                : 'text.primary'
                            }}
                          >
                            {result.result}
                          </Typography>
                          {result.result_alt && result.unit_alt && (
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', display: 'block', fontWeight: 500, mt: 0.25 }}
                            >
                              ↔ {result.result_alt} {result.unit_alt}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {result.unit}
                          {result.unit_alt && (
                            <Typography component="span" sx={{ display: 'block', fontSize: '0.72rem', color: 'text.disabled' }}>
                              + {result.unit_alt}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {result.range}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={result.flag}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: result.flag === 'normal'
                                ? 'rgba(46, 125, 50, 0.1)'
                                : result.flag === 'high'
                                ? 'rgba(211, 47, 47, 0.1)'
                                : result.flag === 'low'
                                ? 'rgba(237, 108, 2, 0.1)'
                                : 'rgba(0, 0, 0, 0.08)',
                              color: result.flag === 'normal'
                                ? '#2e7d32'
                                : result.flag === 'high'
                                ? '#d32f2f'
                                : result.flag === 'low'
                                ? '#ed6c02'
                                : 'text.primary',
                              border: 'none'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <RangeScale result={result.result} range={result.range} flag={result.flag} />
                        </TableCell>
                        <TableCell>
                          {result.derived ? (
                            <Typography sx={{
                              fontSize: '0.72rem', color: t.textMuted, fontStyle: 'italic',
                            }}>
                              auto · edit inputs
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={0.75}>
                              <Tooltip title="Edit result">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditResult(index)}
                                    disabled={editingIndex !== null}
                                    sx={{
                                      color: t.accent.teal,
                                      '&:hover': { backgroundColor: alpha(t.accent.teal, 0.12) },
                                      '&.Mui-disabled': { color: t.textDim }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Remove result">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveResult(result)}
                                    disabled={editingIndex !== null}
                                    sx={{
                                      color: t.accent.danger,
                                      '&:hover': { backgroundColor: alpha(t.accent.danger, 0.12) },
                                      '&.Mui-disabled': { color: t.textDim }
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Section 4: General Comments */}
          <Paper
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: 2,
              border: `1px solid ${t.borderSubtle}`,
              backgroundColor: t.surface,
              backgroundImage: 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.25,
                background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: 800, fontSize: '0.95rem',
                boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.32)}`
              }}>
                4
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: t.text, letterSpacing: '-0.01em' }}>
                General Comments
              </Typography>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Notes or Comments"
              value={generalComment}
              onChange={(e) => setGeneralComment(e.target.value)}
              placeholder="Enter any general comments, observations, or notes for this lab test..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: t.fieldBg, borderRadius: 1.5,
                  '& fieldset': { borderColor: t.borderSubtle },
                  '&.Mui-focused fieldset': { borderColor: t.accent.teal, borderWidth: 2 }
                }
              }}
            />
          </Paper>
          </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3, py: 2,
        backgroundColor: t.surface,
        borderTop: `1px solid ${t.borderSubtle}`,
        gap: 1.25
      }}>
        {(() => {
          const isNarrative = testLabFormat === 'narrative';
          const filledSections = isNarrative
            ? Object.values(narrativeSections).filter((v) => (v || '').trim().length > 0).length
            : 0;
          const hasImpression = isNarrative && narrativeImpression.trim().length > 0;
          const canSave = isNarrative
            ? (filledSections > 0 || hasImpression)
            : enteredResults.length > 0;
          const readyCount = isNarrative ? filledSections : enteredResults.length;
          const unit = isNarrative ? 'section' : 'result';
          return (
            <>
              <Box sx={{ flex: 1, color: t.textMuted, fontSize: '0.82rem' }}>
                {!canSave
                  ? (isNarrative
                      ? 'Type at least one section (or an Impression) to enable save.'
                      : 'Add at least one result to enable save.')
                  : (isNarrative && filledSections === 0 && hasImpression
                      ? 'Ready to save the Impression.'
                      : `Ready to save ${readyCount} ${unit}${readyCount === 1 ? '' : 's'}.`)}
              </Box>
              <Button
                onClick={() => setResultsDialogOpen(false)}
                variant="outlined"
                sx={{
                  px: 3, py: 1, borderRadius: 1.5,
                  color: t.text,
                  borderColor: t.borderStrong,
                  fontWeight: 600, textTransform: 'none',
                  '&:hover': { backgroundColor: t.rowHover, borderColor: t.borderStrong }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveResults}
                disabled={!canSave}
                sx={{
                  px: 3.5, py: 1, borderRadius: 1.5,
                  background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                  fontWeight: 700, textTransform: 'none',
                  boxShadow: `0 6px 16px ${alpha(t.accent.teal, 0.35)}`,
                  '&:hover': { boxShadow: `0 8px 22px ${alpha(t.accent.teal, 0.45)}` },
                  '&.Mui-disabled': {
                    background: alpha(t.text, 0.08),
                    color: t.textDim,
                    boxShadow: 'none'
                  }
                }}
              >
                💾 Save All Results ({readyCount})
              </Button>
            </>
          );
        })()}
      </DialogActions>
    </Dialog>

    
    
      {/* Documentation Dialog */}
      <Dialog
        open={documentationDialogOpen}
        onClose={handleCloseDocumentation}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 3, py: 2.25,
              color: 'white',
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
              fontWeight: 800, fontSize: '1.1rem',
            }}>
              {(selectedPatient?.fullName || '?')
                .split(/\s+/).filter(Boolean).slice(0, 2)
                .map(n => n[0]).join('').toUpperCase()}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.4px',
                textTransform: 'uppercase', opacity: 0.85, lineHeight: 1,
              }}>
                Clinical Notes
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, mt: 0.4, lineHeight: 1.2 }}>
                Patient Documentation
              </Typography>
              {selectedPatient && (
                <Typography sx={{ fontSize: '0.82rem', opacity: 0.9, mt: 0.25 }}>
                  {selectedPatient.fullName} · ID {selectedPatient.id}
                </Typography>
              )}
            </Box>
            {documentationLoading && <CircularProgress size={24} sx={{ color: 'white' }} />}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'action.hover', p: { xs: 2, md: 3 } }}>
          {documentationData ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {[
                { key: 'complaint', label: 'Chief Complaint',       text: documentationData.complaint, color: '#dc2626', tint: '#fef2f2', badge: '1' },
                { key: 'history',   label: 'History of Complaint',  text: documentationData.history,   color: '#2563eb', tint: '#eff6ff', badge: '2' },
                { key: 'physical',  label: 'Physical Examination',  text: documentationData.physical,  color: '#15803d', tint: '#f0fdf4', badge: '3' },
              ].map((s) => (
                <Paper
                  key={s.key}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid', borderColor: 'divider',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: '0 4px 16px rgba(15,23,42,0.06)' },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.25,
                    borderLeft: '4px solid', borderLeftColor: s.color,
                    bgcolor: s.tint,
                  }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: s.color, color: 'white',
                      fontWeight: 800, fontSize: '0.85rem',
                      boxShadow: `0 2px 6px ${s.color}40`,
                    }}>
                      {s.badge}
                    </Box>
                    <Typography sx={{
                      fontWeight: 700, color: s.color,
                      fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px',
                    }}>
                      {s.label}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 3, py: 2.25 }}>
                    {s.text && String(s.text).trim() ? (
                      <Typography sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.75,
                        color: 'text.primary',
                        fontSize: '0.93rem',
                      }}>
                        {s.text}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.88rem' }}>
                        No {s.label.toLowerCase()} recorded for this patient.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%',
                bgcolor: '#e2e8f0', mx: 'auto', mb: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'text.secondary', fontWeight: 700, fontSize: '1.5rem',
              }}>
                ?
              </Box>
              <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>
                No documentation available
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>
                Nothing has been recorded for this patient yet.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 2, borderTop: '1px solid #e5e9ef' }}>
          <Button
            onClick={handleCloseDocumentation}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 1.75,
              px: 3, boxShadow: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { boxShadow: '0 4px 12px rgba(102,126,234,0.4)' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>



      <LabReportDialog
      open={reportDialogOpen}
      onClose={handleCloseReport}
      reportData={reportData}
      reportLoading={reportLoading}
      selectedPatient={selectedPatient}
      categoryHistograms={printCategoryHistograms}
      categoryNarratives={printCategoryNarratives}
    />

      <MedicalHistoryDialog
        open={medicalHistoryOpen}
        onClose={() => setMedicalHistoryOpen(false)}
        patientData={selectedPatient}
      />

      <LabPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        reportData={printReportData}
        selectedPatient={printPatient}
        userInfo={userInfo}
        companyInfo={companyInfo}
        categoryInterpretations={printCategoryInterpretations}
        categoryHistograms={printCategoryHistograms}
        categoryNarratives={printCategoryNarratives}
        categoryFormats={printCategoryFormats}
        referringDoctor={printReferringDoctor}
      />
    </Box>
  );
};

export default MyPatients;